import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { message, Table, Input as AntInput, InputNumber as AntInputNumber, DatePicker as AntDatePicker, Select as AntSelect, Button as AntButton, ConfigProvider, Popconfirm, Tooltip as AntTooltip, Checkbox as AntCheckbox, Modal as AntModal, Divider as AntDivider, Dropdown as AntDropdown, Image as AntImage } from 'antd';
import koKR from 'antd/locale/ko_KR';
import { CalendarOutlined, DeleteOutlined, CopyOutlined, EditOutlined, CheckOutlined, CloseOutlined, MoreOutlined, InfoCircleOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
dayjs.locale('ko');
import { getOrderInfo, getReservationPriceInfo, getReservationTravelerListBySaleNo, saveReservationTravelerChanges, saveReservationTravelersAfterDelete, toggleTravelerEditLock, getSaleDepositList, getReceiptList01, deleteOrderPayment, getReceiptDetailByTransactionNo, applyReceiptToDeposit, getOrderSaleStatus, updateSaleStatus, getCancelInfo, cancelSale, restoreReservation, updateSaleStatusAdvanced, updateOrdererInfo, updateOrderAgency, updateCancelFee, updateAgencyCommission, getProductIdBySaleNo, getReservationDirectInputUrl, getReservationSaleInfo, deleteReceiptRecord, getSaleCustomerRequestItems, getReservationExtraServices, createBankReceiptRecord, getReservationLogsBySaleNo, createReservationMemoLog, updateReservationMemoLog, deleteReservationMemoLog, getPreDepositInfoBySaleNo, getCashbookList, uploadS3Ocr, saveTravelerPassportCopy, getCustomerListBySaleNoFromRCT, getAgencyList } from '../services/apiService';
import { getAuthHeader, getUserInfo } from '../utils/tokenManager';
import GroupInfoDrawer from '../components/GroupInfoDrawer';
import BulkEditTravelerModal from '../components/BulkEditTravelerModal';
import InvoiceModal from '../components/InvoiceModal';
import TravelerAccordionCard from '../components/TravelerAccordionCard';
import RoomAssignmentModal from '../components/RoomAssignmentModal';
import * as XLSX from 'xlsx';

// Derived product info for linking to detail settings
const productNameFallback = '주문정보';

function formatNumberWithCommas(value) {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString('ko-KR');
}

async function fetchStaffInfo({ companyNo, userNo }) {
  if (!companyNo || !userNo) return null;
  const staffPayload = {
    Table1: [
      { 컬럼명: '@업체번호', 값: String(companyNo) },
      { 컬럼명: '@유저번호', 값: Number(userNo) },
    ],
  };
  const staffUrl = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_S_%EC%82%AC%EC%9B%90%EC%A0%95%EB%B3%B4';
  const staffResponse = await fetch(staffUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify(staffPayload),
  });
  if (!staffResponse.ok) return null;
  const data = await staffResponse.json();
  return data?.table?.[0] || null;
}

function normalizeCellText(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function normalizeCellTextLower(value) {
  return normalizeCellText(value).toLowerCase();
}

function compareYmdString(a, b) {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  const aa = String(a).slice(0, 10);
  const bb = String(b).slice(0, 10);
  return aa.localeCompare(bb);
}

function buildTextColumnFilter(dataIndex) {
  return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8, width: 220 }}>
        <AntInput
          size="small"
          placeholder="검색"
          value={selectedKeys?.[0] ?? ''}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <div className="flex gap-2">
          <AntButton size="small" type="primary" onClick={() => confirm()} style={{ flex: 1 }}>
            적용
          </AntButton>
          <AntButton
            size="small"
            onClick={() => {
              clearFilters && clearFilters();
              confirm();
            }}
            style={{ flex: 1 }}
          >
            초기화
          </AntButton>
        </div>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      const needle = normalizeCellTextLower(value);
      const hay = record?.__norm?.[dataIndex] ?? normalizeCellTextLower(record?.[dataIndex]);
      return hay.includes(needle);
    },
  };
}

function buildNumberRangeColumnFilter(dataIndex) {
  return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
      const cur = (selectedKeys?.[0] && typeof selectedKeys[0] === 'object') ? selectedKeys[0] : {};
      const minVal = cur?.min;
      const maxVal = cur?.max;
      const setRange = (next) => setSelectedKeys(next && (next.min !== undefined || next.max !== undefined) ? [next] : []);
      return (
        <div style={{ padding: 8, width: 260 }}>
          <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
            <AntInputNumber
              size="small"
              placeholder="최소"
              value={minVal}
              onChange={(v) => setRange({ ...cur, min: (v === null || v === undefined) ? undefined : Number(v) })}
              style={{ width: '100%' }}
              formatter={(val) => formatNumberWithCommas(typeof val === 'number' ? val : Number(val || 0))}
              parser={(val) => (val ? val.toString().replace(/,/g, '') : '')}
            />
            <span className="text-xs text-gray-500" style={{ flex: '0 0 auto' }}>~</span>
            <AntInputNumber
              size="small"
              placeholder="최대"
              value={maxVal}
              onChange={(v) => setRange({ ...cur, max: (v === null || v === undefined) ? undefined : Number(v) })}
              style={{ width: '100%' }}
              formatter={(val) => formatNumberWithCommas(typeof val === 'number' ? val : Number(val || 0))}
              parser={(val) => (val ? val.toString().replace(/,/g, '') : '')}
            />
          </div>
          <div className="flex gap-2">
            <AntButton size="small" type="primary" onClick={() => confirm()} style={{ flex: 1 }}>
              적용
            </AntButton>
            <AntButton
              size="small"
              onClick={() => {
                clearFilters && clearFilters();
                confirm();
              }}
              style={{ flex: 1 }}
            >
              초기화
            </AntButton>
          </div>
        </div>
      );
    },
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      const v = record?.__num?.[dataIndex] ?? Number(String(record?.[dataIndex] ?? '').replace(/,/g, ''));
      if (!Number.isFinite(v)) return false;
      const min = (value && typeof value === 'object' && value.min !== undefined) ? Number(value.min) : undefined;
      const max = (value && typeof value === 'object' && value.max !== undefined) ? Number(value.max) : undefined;
      if (min !== undefined && v < min) return false;
      if (max !== undefined && v > max) return false;
      return true;
    },
  };
}

function compareKoreanLike(a, b) {
  const sa = normalizeCellText(a);
  const sb = normalizeCellText(b);
  return sa.localeCompare(sb, 'ko-KR');
}

function compareNumberLike(a, b) {
  const na = Number(String(a ?? '').replace(/,/g, ''));
  const nb = Number(String(b ?? '').replace(/,/g, ''));
  const va = Number.isFinite(na) ? na : 0;
  const vb = Number.isFinite(nb) ? nb : 0;
  return va - vb;
}

function compareYmdLike(a, b) {
  const da = dayjs(normalizeCellText(a), ['YYYY-MM-DD', 'YYYY.MM.DD', 'YYYY/MM/DD'], true);
  const db = dayjs(normalizeCellText(b), ['YYYY-MM-DD', 'YYYY.MM.DD', 'YYYY/MM/DD'], true);
  const va = da.isValid() ? da.valueOf() : 0;
  const vb = db.isValid() ? db.valueOf() : 0;
  return va - vb;
}

// Local storage helpers to remember newly-added traveler ids per sale
function getNewlyAddedStorageKey(saleNo) {
  return `reservation:newlyAdded:${saleNo}`;
}
function loadNewlyAddedIds(saleNo) {
  try {
    const raw = localStorage.getItem(getNewlyAddedStorageKey(saleNo));
    const arr = raw ? JSON.parse(raw) : [];
    return new Set((Array.isArray(arr) ? arr : []).map((x) => String(x)));
  } catch { return new Set(); }
}
function saveNewlyAddedIds(saleNo, idSet) {
  try {
    const arr = Array.from(idSet || []).map((x) => String(x));
    localStorage.setItem(getNewlyAddedStorageKey(saleNo), JSON.stringify(arr));
  } catch { }
}

// Display helper: if age group is numeric, append '세'
function formatAgeGroupDisplay(val) {
  const s = (val ?? '').toString().trim();
  if (/^\d+$/.test(s)) return `${s}세`;
  return s;
}

function buildPassportObjectUrl(rawPath) {
  if (!rawPath) return '';
  const s = String(rawPath);
  if (/^https?:\/\//i.test(s)) return s;
  const base = 'https://kr.object.iwinv.kr/orgin/';
  if (s.startsWith('/')) return base + s.slice(1);
  return base + s;
}

function normalizeOcrGender(val) {
  const s = String(val || '').trim().toUpperCase();
  if (!s) return '';
  if (s === 'F' || s === 'W' || s === 'FEMALE' || s === 'WOMAN') return '여성';
  if (s === 'M' || s === 'MALE' || s === 'MAN') return '남성';
  if (s.includes('여')) return '여성';
  if (s.includes('남')) return '남성';
  return String(val || '').trim();
}

function normalizeOcrToTravelerPatch(ocr) {
  const o = ocr && typeof ocr === 'object' ? ocr : {};
  const patch = {};
  const pickFirst = (...vals) => {
    for (const v of vals) {
      const s = String(v ?? '').trim();
      if (s) return s;
    }
    return '';
  };
  const enLast = pickFirst(o?.성, o?.surname, o?.lastName, o?.familyName);
  const enFirst = pickFirst(o?.이름, o?.givenName, o?.firstName);
  const koName = pickFirst(o?.한글이름, o?.koreanName, o?.korean_name, o?.nameKo);
  const passportNo = pickFirst(o?.여권번호, o?.passportNumber, o?.passportNo, o?.passport_no);
  const birth = pickFirst(o?.생년월일, o?.dateOfBirth, o?.birthDate, o?.dob);
  const exp = pickFirst(o?.만료일, o?.dateOfExpiry, o?.expiryDate, o?.expirationDate, o?.expiresAt);
  const gender = normalizeOcrGender(pickFirst(o?.성별, o?.sex, o?.gender));

  if (koName) patch.한글이름 = koName;
  if (enLast) patch.영문성 = enLast;
  if (enFirst) patch.영문이름 = enFirst;
  if (passportNo) patch.여권번호 = passportNo;
  if (birth) patch.생년월일 = birth;
  if (exp) patch.여권만료일 = exp;
  if (gender) patch.성별 = gender;

  return patch;
}

// Inline date editor: text input (auto-hyphen) + calendar icon to open DatePicker panel
function DateFieldEditor({ value, onChange, placeholder = 'YYYY-MM-DD', width = '100%', disabled = false }) {
  const [open, setOpen] = React.useState(false);
  const normalized = normalizeYmdInitial(value);

  React.useEffect(() => {
    const next = normalizeYmdInitial(value);
    if (next && next !== value) {
      onChange(next);
    }
  }, [value, onChange]);

  const str = normalized ?? '';
  const invalid = str ? !isValidYmd(str) : false;
  const dayVal = str && isValidYmd(str) ? dayjs(str, 'YYYY-MM-DD') : null;
  return (
    <div className="relative" style={{ width }}>
      <AntInput
        size="small"
        placeholder={placeholder}
        value={str}
        inputMode="numeric"
        maxLength={10}
        status={invalid ? 'error' : ''}
        onChange={(e) => onChange(formatYmdInput(e.target.value))}
        style={{ textAlign: 'center' }}
        disabled={disabled}
        suffix={!disabled && (
          <CalendarOutlined
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            style={{ color: '#64748b', cursor: 'pointer' }}
            title="달력 열기"
          />
        )}
      />
      {/* Hidden DatePicker input, but its panel is used. Controlled by open state. */}
      <AntDatePicker
        open={open}
        onOpenChange={(o) => setOpen(o)}
        value={dayVal}
        format="YYYY-MM-DD"
        showToday={false}
        onChange={(_d, dateString) => {
          onChange(formatYmdInput(dateString));
          setOpen(false);
        }}
        // Keep the trigger within the container so the popup positions correctly
        // Do not move it offscreen; make it invisible and non-interactive instead
        style={{ position: 'absolute', top: 0, right: 0, width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
        disabled={disabled}
      />
      {invalid && <div className="text-[11px] text-red-600 mt-1">잘못된 날짜입니다</div>}
    </div>
  );
}

// Auto-format YYYY-MM-DD while typing: keep digits, cap at 8, insert '-' after YYYY and YYYYMM
function formatYmdInput(raw) {
  const digits = String(raw || '').replace(/\D+/g, '').slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
}

function normalizeYmdInitial(raw) {
  const s = (raw ?? '').toString().trim();
  if (!s) return '';
  if (/^\d{8}$/.test(s)) return formatYmdInput(s);
  return s;
}

// Validate YYYY-MM-DD (basic range + month/day checks incl. leap year)
function isValidYmd(str) {
  if (!str || typeof str !== 'string') return false;
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (y < 1900 || y > 2100) return false;
  if (mo < 1 || mo > 12) return false;
  const mdays = [31, (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const maxd = mdays[mo - 1];
  if (d < 1 || d > maxd) return false;
  return true;
}

// Generate a random UUID-like string for temp traveler id
function generateTempTravelerId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
  // xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  return (
    s4() + s4() + '-' + s4() + '-4' + s4().substr(0, 3) + '-' + ((8 + Math.random() * 4) | 0).toString(16) + s4().substr(0, 3) + '-' + s4() + s4() + s4()
  );
}

const ReservationDetail = () => {
  const [isAdminUser, setIsAdminUser] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { orderNo } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  // Group detail drawer state
  const [groupOpen, setGroupOpen] = useState(false);
  // Price info for this reservation
  const [priceInfo, setPriceInfo] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState(null);
  // Cancel fee inline edit state
  const [cancelFeeEditing, setCancelFeeEditing] = useState(false);
  const [cancelFeeDraft, setCancelFeeDraft] = useState(0);
  const [cancelFeeSaving, setCancelFeeSaving] = useState(false);
  // Agency commission edit modal state
  const [agencyCommissionModalOpen, setAgencyCommissionModalOpen] = useState(false);
  const [agencyCommissionDraft, setAgencyCommissionDraft] = useState(0);
  const [agencyCommissionSaving, setAgencyCommissionSaving] = useState(false);
  // Payment edit modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentHelpOpen, setPaymentHelpOpen] = useState(false);
  const [depositList, setDepositList] = useState([]);
  const [depositSummary, setDepositSummary] = useState(null);
  const [receiptList, setReceiptList] = useState([]);
  const [receiptRangePreset, setReceiptRangePreset] = useState('3m');
  const [receiptStartDate, setReceiptStartDate] = useState(() => dayjs().subtract(3, 'month').format('YYYY-MM-DD'));
  const [receiptEndDate, setReceiptEndDate] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);

  const receiptTableData = useMemo(() => {
    const list = Array.isArray(receiptList) ? receiptList : [];
    return list.map((r, idx) => {
      const tx = r?.거래번호;
      const key = (tx !== null && tx !== undefined && String(tx).trim() !== '') ? String(tx) : `idx-${idx}`;
      return {
        key,
        ...r,
        __norm: {
          수납일: normalizeCellTextLower(r?.수납일),
          송급카드소유자: normalizeCellTextLower(r?.송급카드소유자),
          종류: normalizeCellTextLower(r?.종류),
        },
        __num: {
          수납액: Number(String(r?.수납액 ?? '').replace(/,/g, '')),
          총입금액: Number(String(r?.총입금액 ?? '').replace(/,/g, '')),
          잔액: Number(String(r?.잔액 ?? '').replace(/,/g, '')),
        },
      };
    });
  }, [receiptList]);
  // Receipt selection and apply state
  const [selectedReceiptTxNo, setSelectedReceiptTxNo] = useState(null);
  const [selectedReceiptRow, setSelectedReceiptRow] = useState(null);
  const [receiptDetail, setReceiptDetail] = useState(null);
  const [applyAmount, setApplyAmount] = useState(null);
  const [applyNote, setApplyNote] = useState('');
  const [applying, setApplying] = useState(false);
  const [cashExclude, setCashExclude] = useState(false);
  // Orderer edit state
  const [ordererEditing, setOrdererEditing] = useState(false);
  const [ordererName, setOrdererName] = useState('');
  const [ordererPhone, setOrdererPhone] = useState('');
  const [ordererEmail, setOrdererEmail] = useState('');
  const [orderAgencyNo, setOrderAgencyNo] = useState('');
  const [orderAgencyName, setOrderAgencyName] = useState('');
  const [agencySelectOpen, setAgencySelectOpen] = useState(false);
  const [agencySelectLoading, setAgencySelectLoading] = useState(false);
  const [agencySelectError, setAgencySelectError] = useState('');
  const [agencySelectList, setAgencySelectList] = useState([]);
  const [agencySelectQuery, setAgencySelectQuery] = useState('');
  const [ordererSaving, setOrdererSaving] = useState(false);
  // Sale status & cancel flag
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [saleStatusRow, setSaleStatusRow] = useState(null); // API row
  const [cancelLocal, setCancelLocal] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);
  // Cancel flow modal state
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelInfoLoading, setCancelInfoLoading] = useState(false);
  const [cancelInfoError, setCancelInfoError] = useState(null);
  const [cancelInfoRow, setCancelInfoRow] = useState(null);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [reservationLogs, setReservationLogs] = useState([]);
  // 업무메모 수정 모달
  const [memoEditOpen, setMemoEditOpen] = useState(false);
  const [memoEditRow, setMemoEditRow] = useState(null);
  const [memoEditText, setMemoEditText] = useState('');
  const [memoSaving, setMemoSaving] = useState(false);
  const [memoDeleting, setMemoDeleting] = useState(false);
  // 업무메모 추가 모달
  const [memoAddOpen, setMemoAddOpen] = useState(false);
  const [memoAddText, setMemoAddText] = useState('');
  const [memoAdding, setMemoAdding] = useState(false);
  const [cancelFee, setCancelFee] = useState(0);
  const [cancelSaving, setCancelSaving] = useState(false);
  const [saleInfoRow, setSaleInfoRow] = useState(null);
  // Traveler list state
  const [travelerLoading, setTravelerLoading] = useState(false);
  const [travelerError, setTravelerError] = useState(null);
  const [travelers, setTravelers] = useState([]);
  const [saving, setSaving] = useState(false);
  // Keep track of traveler IDs that were added from this client
  const [newlyAddedIds, setNewlyAddedIds] = useState(new Set());
  const [addTravelerAgeModalOpen, setAddTravelerAgeModalOpen] = useState(false);
  const [addTravelerAge, setAddTravelerAge] = useState('성인');
  // Edit lock for traveler list (명단편집 불가)
  const [editLocked, setEditLocked] = useState(false);
  // Resolved product id via SP (when not present in info/state)
  const [resolvedProductId, setResolvedProductId] = useState(null);
  // URL 생성 로딩/결과 상태
  const [urlGenerating, setUrlGenerating] = useState(false);
  const [lastGeneratedUrl, setLastGeneratedUrl] = useState('');
  // 여행객 일괄수정 상태
  const [selectedTravelerKeys, setSelectedTravelerKeys] = useState([]);
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  // 수납 항목 추가 상태
  const [showAddReceiptForm, setShowAddReceiptForm] = useState(false);
  const [newReceiptDate, setNewReceiptDate] = useState('');
  const [newReceiptAmount, setNewReceiptAmount] = useState(null);
  const [newReceiptDepositor, setNewReceiptDepositor] = useState('');
  const [newReceiptNote, setNewReceiptNote] = useState('');
  const [addingReceipt, setAddingReceipt] = useState(false);
  const [applyImmediately, setApplyImmediately] = useState(true);
  const [deletingReceiptTxNo, setDeletingReceiptTxNo] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceDepositSummary, setInvoiceDepositSummary] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(false);
  const [bankAccountsError, setBankAccountsError] = useState('');

  const handleExportTravelersExcel = () => {
    try {
      const list = Array.isArray(travelers) ? travelers : [];
      if (list.length === 0) {
        message.error('내보낼 여행객이 없습니다.');
        return;
      }

      const rows = list.map((t, idx) => {
        const basePrice = Number(t?.상품금액원화) || 0;
        const extraSalePrice = Number(t?.택스및유류원화) || 0;
        return {
          'No': idx + 1,
          '여행객번호': t?.여행객번호 ?? t?.여행객키 ?? '',
          '여행객종류': t?.여행객종류 ?? '',
          '연령구분': t?.연령구분 ?? '',
          '한글이름': t?.한글이름 ?? '',
          '성별': t?.성별 ?? '',
          '영문성': t?.영문성 ?? '',
          '영문이름': t?.영문이름 ?? '',
          '생년월일': t?.생년월일 ?? '',
          '여권번호': t?.여권번호 ?? '',
          '여권만료일': t?.여권만료일 ?? '',
          '휴대폰': t?.휴대폰 ?? '',
          '이메일': t?.이메일 ?? '',
          '룸번호': t?.룸번호 ?? '',
          '상품금액(원)': basePrice,
          '추가판매가(원)': extraSalePrice,
          '총판매가(원)': basePrice + extraSalePrice,
          '여행객비고': t?.여행객비고 ?? '',
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, '여행객');

      const safeSaleNo = String(saleNo ?? '').trim();
      const safeOrderNo = String(orderNo ?? '').trim();
      const baseName = safeSaleNo ? `예약_${safeSaleNo}` : (safeOrderNo ? `예약_${safeOrderNo}` : '예약');
      XLSX.writeFile(wb, `${baseName}_여행객.xlsx`);
    } catch (e) {
      try { console.error('[ReservationDetail] export travelers excel failed', e); } catch { }
      message.error('엑셀 내보내기에 실패했습니다.');
    }
  };
  const [newReceiptAccountId, setNewReceiptAccountId] = useState('');
  // 고객 요청사항
  const [requestItems, setRequestItems] = useState([]);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState(null);
  // Extra services (추가 서비스/옵션)
  const [extraServices, setExtraServices] = useState([]);
  const [extraLoading, setExtraLoading] = useState(false);
  const [extraError, setExtraError] = useState(null);
  const [extraModalOpen, setExtraModalOpen] = useState(false);
  // Accordion expansion state for traveler cards
  const [expandedTravelers, setExpandedTravelers] = useState(new Set());
  const [passportUploadingKey, setPassportUploadingKey] = useState(null);
  const [passportOcrModalOpen, setPassportOcrModalOpen] = useState(false);
  const [passportOcrTargetKey, setPassportOcrTargetKey] = useState(null);
  const [passportOcrRaw, setPassportOcrRaw] = useState(null);
  const [passportOcrPatch, setPassportOcrPatch] = useState({});
  const [passportOcrApply, setPassportOcrApply] = useState({});
  const [passportOcrSaving, setPassportOcrSaving] = useState(false);
  // Traveler-level cancel fee modal state
  const [travelerCancelModalOpen, setTravelerCancelModalOpen] = useState(false);
  const [travelerCancelTargetKey, setTravelerCancelTargetKey] = useState(null);
  const [travelerCancelAmount, setTravelerCancelAmount] = useState(0);
  // Traveler-level restore (un-cancel) modal state
  const [travelerRestoreModalOpen, setTravelerRestoreModalOpen] = useState(false);
  const [travelerRestoreTargetKey, setTravelerRestoreTargetKey] = useState(null);
  const [travelerRestorePrice, setTravelerRestorePrice] = useState(0);
  const [travelerRestoreTaxFuel, setTravelerRestoreTaxFuel] = useState(0);
  // Room assignment modal state
  const [roomModalOpen, setRoomModalOpen] = useState(false);

  const ageOptionsForAdd = useMemo(() => (
    [
      { value: '성인', label: '성인' },
      ...Array.from({ length: 19 }, (_, n) => ({ value: String(n), label: `${n}세` })),
    ]
  ), []);

  const normalizeAgeValue = (val) => String(val ?? '').trim();
  const isNumericAgeValue = (val) => /^\d+$/.test(normalizeAgeValue(val));
  const buildAgeArrayFromTravelers = (list) => {
    const ages = (Array.isArray(list) ? list : [])
      .map((t) => normalizeAgeValue(t?.연령구분))
      .filter((s) => /^\d+$/.test(s))
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n) && n >= 0)
      .sort((a, b) => a - b);
    return ages.join(',');
  };
  const buildAdultCountFromTravelers = (list) => {
    return (Array.isArray(list) ? list : []).filter((t) => {
      const age = normalizeAgeValue(t?.연령구분);
      if (!age) return true;
      if (age.includes('성인')) return true;
      return !isNumericAgeValue(age);
    }).length;
  };

  const confirmAddTravelerWithAge = async () => {
    if (!saleNo) {
      message.error('판매번호가 없어 명단 추가를 반영할 수 없습니다.');
      return;
    }
    const selectedAge = (addTravelerAge ?? '성인');
    if (!String(selectedAge).trim()) {
      message.error('예약연령을 선택하세요.');
      return;
    }

    setAddTravelerAgeModalOpen(false);

    const newKey = `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const tempId = generateTempTravelerId();
    const next = [
      ...travelers,
      {
        key: newKey,
        여행객번호: tempId, // temporary id to be assigned by server after save
        여행객종류: '여행객',
        순번: (travelers?.length || 0) + 1,
        연령구분: selectedAge,
        한글이름: '',
        성별: undefined,
        영문성: '',
        영문이름: '',
        생년월일: '',
        여권번호: '',
        여권만료일: '',
        여행객비고: '',
        상품금액원화: 0,
        공급단가: 0,
        택스및유류원화: 0,
      },
    ];

    // Optimistic local update
    setTravelers(next);

    // Build minimal JSON including existing numeric travelers and the new temp traveler
    const entries = [];
    (next || []).forEach((t) => {
      const travelerNoRaw = t?.여행객번호 || t?.여행객키;
      if (!travelerNoRaw) return;
      const age = t?.연령구분 || '성인';
      const push = (fieldNo, val) => {
        if (val == null) val = '';
        // allow empty string for new traveler fields
        entries.push({ 여행객번호: travelerNoRaw, 필드번호: fieldNo, 필드값: String(val), 연령: String(age) });
      };
      // 최소 필드: 한글이름(8), 성별(109), 영문성(9), 영문이름(10)
      push(8, t?.한글이름 || '');
      push(109, t?.성별 || '남성'); // 기본값 예시와 동일하게 남성으로 지정
      push(9, t?.영문성 || '');
      push(10, t?.영문이름 || '');
    });

    const adultCount = buildAdultCountFromTravelers(next);
    const ageArray = buildAgeArrayFromTravelers(next);
    const hide = message.loading('명단 추가 반영 중...', 0);
    try {
      // Capture current ids before server assigns new ones
      const prevIdSet = new Set((travelers || []).map((r) => String(r?.여행객번호 || r?.여행객키 || '')));
      await saveReservationTravelersAfterDelete({
        saleNo,
        minimalTravelerRows: entries,
        adultCount,
        ageArray,
      });
      hide();
      message.success('명단 추가가 반영되었습니다.');
      // Refresh from server to get assigned traveler numbers
      const updated = await getReservationTravelerListBySaleNo({ saleNo });
      const withKeys = (Array.isArray(updated) ? updated : []).map((r, idx) => ({ ...r, key: String(r?.여행객번호 || r?.여행객키 || idx) }));
      setTravelers(withKeys);
      // Compute newly assigned ids that were not in prev set and mark them as newly added
      const afterIds = new Set(withKeys.map((r) => String(r?.여행객번호 || r?.여행객키 || '')));
      const gained = new Set(Array.from(afterIds).filter((id) => !prevIdSet.has(String(id))));
      const merged = new Set([...Array.from(newlyAddedIds), ...Array.from(gained)]);
      setNewlyAddedIds(merged);
      saveNewlyAddedIds(saleNo, merged);

      // Refresh price/unpaid related info without full page reload
      try {
        const [latestPrice, latestInfo] = await Promise.all([
          getReservationPriceInfo({ saleNo }).catch(() => null),
          orderNo ? getOrderInfo({ orderNo }).catch(() => null) : Promise.resolve(null),
        ]);
        if (latestPrice) setPriceInfo(latestPrice);
        if (latestInfo) setInfo(latestInfo);
      } catch (e2) {
        try { console.warn('[ReservationDetail] failed to refresh price/order info after add traveler', e2); } catch { }
      }

      // If payment modal is open, also refresh deposit/receipt summary so "현재 미수금" stays in sync
      try {
        if (paymentModalOpen) {
          await refreshPaymentModalData();
        }
      } catch (e3) {
        try { console.warn('[ReservationDetail] failed to refresh payment modal after add traveler', e3); } catch { }
      }
    } catch (e) {
      hide();
      console.error(e);
      message.error(e?.message || '명단 추가 반영 중 오류가 발생했습니다.');
    }
  };
  // Pre-deposit (단체 전도금) section state
  const [preDepExpanded, setPreDepExpanded] = useState(true);
  const [preDepLoading, setPreDepLoading] = useState(false);
  const [preDepError, setPreDepError] = useState(null);
  const [preDepRows, setPreDepRows] = useState([]);
  const [preDepSummary, setPreDepSummary] = useState(null);
  const [cashbookAllRows, setCashbookAllRows] = useState([]);
  const [cashbookMappingRows, setCashbookMappingRows] = useState([]);
  const [preDepModalOpen, setPreDepModalOpen] = useState(false);
  const [groupPreDepAutoOpen, setGroupPreDepAutoOpen] = useState(false);
  const [directPreDepositMode, setDirectPreDepositMode] = useState(false);

  const { companyNo: currentCompanyNo } = getUserInfo() || {};
  const isPassportUploadAllowed = true; // Allow all users to upload passport copies

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { companyNo, userNo } = getUserInfo() || {};
        const staff = await fetchStaffInfo({ companyNo, userNo });
        if (!mounted) return;
        setIsAdminUser(!!(staff && staff.관리자여부));
      } catch {
        if (!mounted) return;
        setIsAdminUser(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const openAgencySelectModal = async () => {
    setAgencySelectOpen(true);
    setAgencySelectError('');
    setAgencySelectQuery('');
    try {
      setAgencySelectLoading(true);
      const { companyNo } = getUserInfo() || {};
      const list = await getAgencyList({ companyNo });
      setAgencySelectList(Array.isArray(list) ? list : []);
    } catch (e) {
      setAgencySelectList([]);
      setAgencySelectError(e?.message || '거래처 목록을 불러오지 못했습니다.');
    } finally {
      setAgencySelectLoading(false);
    }
  };

  const filteredAgencySelectList = useMemo(() => {
    const q = String(agencySelectQuery || '').trim().toLowerCase();
    if (!q) return agencySelectList || [];
    return (agencySelectList || []).filter((row) => {
      const code = String(row?.코드 ?? row?.업체번호2 ?? row?.거래처번호 ?? '').toLowerCase();
      const name = String(row?.코드명 ?? row?.거래처명 ?? '').toLowerCase();
      const phone = String(row?.대표전화 ?? row?.전화번호 ?? '').toLowerCase();
      const manager = String(row?.담당자명 ?? row?.담당자 ?? '').toLowerCase();
      return code.includes(q) || name.includes(q) || phone.includes(q) || manager.includes(q);
    });
  }, [agencySelectList, agencySelectQuery]);

  // Product link info
  const productName = useMemo(() => (
    location?.state?.productName || info?.판매상품명 || productNameFallback
  ), [location?.state?.productName, info?.판매상품명]);
  // Prefer product id from navigation state or order info
  const baseProductId = useMemo(() => {
    return (
      location?.state?.productId ||
      info?.상품ID || info?.상품아이디 || info?.상품번호 || info?.상품코드 ||
      info?.판매상품번호 || info?.판매상품ID || info?.판매상품아이디 || null
    );
  }, [location?.state?.productId, info]);
  // Final product id used for navigation (fallback to resolved via SP)
  const productIdForSettings = baseProductId || resolvedProductId;
  const goToDetailSettings = () => {
    if (!productIdForSettings) { message.warning('상품 ID가 없어 상세페이지 설정으로 이동할 수 없습니다.'); return; }
    navigate(`/detail-settings/${productIdForSettings}`);
  };

  // Status badge style by label
  const statusBadgeClass = useMemo(() => {
    const label = (saleStatusRow?.판매상태 || '').toString();
    if (label.includes('예약확정')) return 'border-green-200 bg-green-50 text-green-700';
    if (label.includes('예약접수')) return 'border-blue-200 bg-blue-50 text-blue-700';
    if (label.includes('예약불가')) return 'border-gray-300 bg-gray-100 text-gray-700';
    if (label.includes('취소완료')) return 'border-red-300 bg-red-100 text-red-800';
    if (label.includes('취소확정')) return 'border-red-200 bg-red-50 text-red-700';
    if (label.includes('취소')) return 'border-red-200 bg-red-50 text-red-700';
    return 'border-gray-200 bg-gray-50 text-gray-700';
  }, [saleStatusRow]);

  const isOrderCancelled = useMemo(() => {
    const statusText = String(saleStatusRow?.판매상태 ?? '').trim();
    return (
      (saleStatusRow?.취소여부 === 1) ||
      String(saleStatusRow?.취소여부) === '1' ||
      cancelLocal === '1' ||
      statusText.startsWith('취소')
    );
  }, [saleStatusRow, cancelLocal]);

  const resolveReceiptRange = (preset) => {
    const end = dayjs().format('YYYY-MM-DD');
    if (preset === 'today') {
      return { startDate: end, endDate: end };
    }
    if (preset === '1w') {
      return { startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'), endDate: end };
    }
    if (preset === '1m') {
      return { startDate: dayjs().subtract(1, 'month').format('YYYY-MM-DD'), endDate: end };
    }
    if (preset === '3m') {
      return { startDate: dayjs().subtract(3, 'month').format('YYYY-MM-DD'), endDate: end };
    }
    return { startDate: receiptStartDate, endDate: receiptEndDate };
  };

  // Helper to (re)load payment-related lists
  const refreshPaymentModalData = async (rangeOverride) => {
    if (!saleNo) return;
    setPaymentLoading(true);
    setPaymentError(null);
    try {
      const startDate = rangeOverride?.startDate || receiptStartDate;
      const endDate = rangeOverride?.endDate || receiptEndDate;
      const depositRes = await getSaleDepositList({ saleNo });
      const receipts = await getReceiptList01({ saleNo, startDate, endDate });
      setDepositList(Array.isArray(depositRes?.list) ? depositRes.list : []);
      setDepositSummary(depositRes?.summary || null);
      const filteredReceipts = (Array.isArray(receipts) ? receipts : []).filter((r) => Number(r?.잔액) !== 0);
      setReceiptList(filteredReceipts);
      // reset selection and apply form on refresh
      setSelectedReceiptTxNo(null);
      setReceiptDetail(null);
      setApplyAmount(null);
      setApplyNote('');
      setCashExclude(false);
    } catch (err) {
      console.error(err);
      setPaymentError(err?.message || '결제 정보를 불러오지 못했습니다.');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Open payment edit modal and fetch lists
  const openPaymentModal = async () => {
    if (!saleNo) {
      message.error('판매번호가 없어 결제 정보를 불러올 수 없습니다.');
      return;
    }
    setPaymentModalOpen(true);
    await Promise.all([
      refreshPaymentModalData(),
      loadCompanyBankAccounts(),
    ]);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setShowAddReceiptForm(false);
    resetNewReceiptForm();
  };

  const applyReceiptRangePreset = async (preset) => {
    setReceiptRangePreset(preset);
    if (preset === 'custom') return;
    const next = resolveReceiptRange(preset);
    setReceiptStartDate(next.startDate);
    setReceiptEndDate(next.endDate);
    await refreshPaymentModalData(next);
  };

  const onChangeReceiptCustomRange = async (dates) => {
    const start = dates?.[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : '';
    const end = dates?.[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : '';
    setReceiptStartDate(start);
    setReceiptEndDate(end);
    if (!start || !end) return;
    if (!isValidYmd(start) || !isValidYmd(end)) return;
    if (compareYmdString(start, end) > 0) return;
    await refreshPaymentModalData({ startDate: start, endDate: end });
  };

  const loadCompanyBankAccounts = async () => {
    try {
      setBankAccountsLoading(true);
      setBankAccountsError('');
      const { companyNo } = getUserInfo() || {};
      const payload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: String(companyNo ?? '1') },
        ],
      };
      const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.RCT_S_%ED%9A%8C%EC%82%AC%EA%B8%B0%EB%B3%B8%EC%A0%95%EB%B3%B4';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      const list = Array.isArray(data?.table8) ? data.table8 : [];
      setBankAccounts(list);
      if (!newReceiptAccountId && list.length > 0) {
        const first = list[0];
        setNewReceiptAccountId(String(first.계좌일련번호 ?? ''));
      }
    } catch (e) {
      setBankAccounts([]);
      setBankAccountsError('입금 계좌 정보를 불러오지 못했습니다.');
    } finally {
      setBankAccountsLoading(false);
    }
  };

  // Reset new receipt form
  const resetNewReceiptForm = () => {
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    setNewReceiptDate(todayStr);
    setNewReceiptAmount(null);
    setNewReceiptDepositor('');
    setNewReceiptNote('');
    setApplyImmediately(true);
    setNewReceiptAccountId('');
  };

  // Handle add new receipt
  const handleAddReceipt = async () => {
    // Validation
    if (!newReceiptDate || !isValidYmd(newReceiptDate)) {
      message.error('올바른 수납일을 입력하세요.');
      return;
    }
    const amount = Number(newReceiptAmount);
    if (!amount || amount <= 0) {
      message.error('수납액을 입력하세요.');
      return;
    }
    if (!newReceiptAccountId) {
      message.error('입금 계좌를 선택하세요.');
      return;
    }
    if (!newReceiptDepositor?.trim()) {
      message.error('입금자를 입력하세요.');
      return;
    }

    try {
      setAddingReceipt(true);
      const hide = message.loading('수납 등록 중...', 0);
      const account = (bankAccounts || []).find((a) => String(a?.계좌일련번호 ?? '') === String(newReceiptAccountId));
      if (!account) {
        hide();
        message.error('선택한 입금 계좌 정보를 찾을 수 없습니다.');
        return;
      }
      await createBankReceiptRecord({
        transactionDate: newReceiptDate,
        amount,
        paymentMethodCode: '254001',
        accountNo: account.계좌번호,
        bankCode: account.은행,
        senderName: newReceiptDepositor.trim(),
        note: newReceiptNote.trim(),
      });
      hide();

      // Refresh receipt list to get the new transaction number
      await refreshPaymentModalData();

      // If apply immediately is checked, find and apply the new receipt
      if (applyImmediately && saleNo) {
        try {
          const hide2 = message.loading('수납 적용 중...', 0);

          // Fetch updated receipt list
          let startDate = receiptStartDate;
          let endDate = receiptEndDate;
          if (newReceiptDate && isValidYmd(newReceiptDate)) {
            if (startDate && compareYmdString(newReceiptDate, startDate) < 0) startDate = newReceiptDate;
            if (endDate && compareYmdString(newReceiptDate, endDate) > 0) endDate = newReceiptDate;
          }
          const receipts = await getReceiptList01({ saleNo, startDate, endDate });

          // Find the newly created receipt (match by date, amount, and depositor)
          const newReceipt = (Array.isArray(receipts) ? receipts : []).find((r) => {
            const matchDate = String(r?.수납일 || '').includes(newReceiptDate);
            const matchAmount = Number(r?.수납액) === amount;
            const matchDepositor = String(r?.송급카드소유자 || '').includes(newReceiptDepositor.trim());
            return matchDate && matchAmount && matchDepositor;
          });

          if (newReceipt?.거래번호) {
            const txNo = newReceipt.거래번호;
            const detail = await getReceiptDetailByTransactionNo({ saleNo, transactionNo: txNo });
            const maxByBalance = Number(detail?.잔액) || 0;
            const maxByDue = Number(depositSummary?.미수금) || 0;
            const applyAmt = Math.max(0, Math.min(maxByBalance, maxByDue, amount));

            if (applyAmt > 0) {
              await applyReceiptToDeposit({
                saleNo,
                transactionNo: txNo,
                amount: applyAmt,
                note: newReceiptNote.trim(),
                currencyCode: detail?.수납통화코드 || '058001',
                cashReceiptSelfExclude: false,
              });
              hide2();
              message.success('수납이 등록되고 적용되었습니다.');
            } else {
              hide2();
              message.success('수납이 등록되었습니다. (적용 가능 금액 없음)');
            }
          } else {
            hide2();
            message.success('수납이 등록되었습니다.');
          }

          // Final refresh
          await refreshPaymentModalData();
        } catch (applyErr) {
          console.error('수납 적용 실패', applyErr);
          message.warning('수납은 등록되었으나 자동 적용에 실패했습니다. 수동으로 적용해주세요.');
        }
      } else {
        message.success('수납이 등록되었습니다.');
      }

      setShowAddReceiptForm(false);
      resetNewReceiptForm();
    } catch (e) {
      console.error(e);
      message.error(e?.message || '수납 등록에 실패했습니다.');
    } finally {
      setAddingReceipt(false);
    }
  };

  // Save handler to persist traveler edits
  const handleSave = async (customTravelers) => {
    if (saleNo == null) return;
    try {
      setSaving(true);
      // Build traveler JSON arrays
      const fieldRows = [];
      const amountRows = [];
      const orderRows = [];
      const source = Array.isArray(customTravelers)
        ? customTravelers
        : (Array.isArray(travelers) ? travelers : []);
      source.forEach((t) => {
        const travelerNo = Number(t?.여행객번호 || t?.여행객키);
        if (!travelerNo) return;
        const pushField = (fieldNo, val) => fieldRows.push({ 여행객번호: travelerNo, 필드번호: fieldNo, 값: val });
        // 필드 매핑
        pushField(8, t?.한글이름 ?? null); // 한글이름
        pushField(-1, t?.룸번호 ?? null); // 룸번호
        pushField(109, t?.성별 ?? null); // 성별
        pushField(9, t?.영문성 ?? null); // 영문성
        pushField(10, t?.영문이름 ?? null); // 영문이름
        pushField(11, t?.생년월일 ?? null); // 생년월일
        pushField(13, null);
        pushField(253, t?.여권번호 ?? null); // 여권번호
        pushField(254, t?.여권만료일 ?? null); // 여권만료일
        pushField(314, t?.여행객비고 ?? null); // 비고
        pushField(12, t?.휴대폰 ?? null);
        pushField(256, t?.이메일 ?? null);
        pushField(404, null); // 주민등록번호 공란
        pushField(255, null);

        // 금액 정보
        amountRows.push({
          여행객번호: travelerNo,
          상품금액: Number(t?.상품금액원화) || 0,
          공급단가: Number(t?.공급단가) || 0,
          택스및유류: Number(t?.택스및유류원화) || 0,
        });

        // 순번/확인 정보: 기존 값이 있으면 사용, 없으면 기본값
        orderRows.push({
          여행객번호: travelerNo,
          여행객종류: t?.여행객종류 ?? '여행객',
          선발권동의: !!t?.선발권동의,
          명단확인: !!t?.명단확인,
          순번: Number.isFinite(Number(t?.순번)) ? Number(t?.순번) : (orderRows.length + 1),
        });
      });

      await saveReservationTravelerChanges({
        saleNo,
        travelerFieldRows: fieldRows,
        travelerAmounts: amountRows,
        travelerOrders: orderRows,
      });
      message.success('저장되었습니다.');
      // Refresh traveler list after save
      const updated = await getReservationTravelerListBySaleNo({ saleNo });
      const withKeys = (Array.isArray(updated) ? updated : []).map((r, idx) => ({ ...r, key: String(r?.여행객번호 || r?.여행객키 || idx) }));
      let merged = withKeys;
      try {
        const rctList = await getCustomerListBySaleNoFromRCT({ saleNo });
        const byTravelerNo = new Map();
        (Array.isArray(rctList) ? rctList : []).forEach((row) => {
          const no = Number(row?.여행객번호);
          if (Number.isFinite(no) && no > 0) {
            byTravelerNo.set(no, row);
          }
        });
        merged = withKeys.map((row) => {
          const no = Number(row?.여행객번호 || row?.여행객키);
          const rctRow = Number.isFinite(no) && no > 0 ? byTravelerNo.get(no) : null;
          if (rctRow && rctRow.여권사본경로) {
            return { ...row, 여권사본경로: rctRow.여권사본경로 };
          }
          return row;
        });
      } catch (e) {
        try { console.warn('[ReservationDetail] failed to merge RCT passport path after save', e); } catch { }
      }
      setTravelers(merged);
      // After a full save, clear 'newly added' markers so subsequent edits are read-only
      const cleared = new Set();
      setNewlyAddedIds(cleared);
      saveNewlyAddedIds(saleNo, cleared);
      // Refresh price info so the top price section reflects latest traveler amounts (including tax/fuel)
      try {
        const latestPrice = await getReservationPriceInfo({ saleNo });
        setPriceInfo(latestPrice || null);
      } catch (e2) {
        try { console.error('[ReservationDetail] failed to refresh price info after traveler save', e2); } catch { }
      }
    } catch (e) {
      console.error(e);
      message.error(e?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadPassport = async (traveler, file) => {
    if (!saleNo) {
      message.error('판매번호가 없어 여권사본을 저장할 수 없습니다.');
      return;
    }
    const travelerNo = Number(traveler?.여행객번호 || traveler?.여행객키);
    if (!Number.isFinite(travelerNo) || travelerNo <= 0) {
      message.error('저장된 여행객에 대해서만 여권사본을 저장할 수 있습니다. 먼저 명단을 저장한 후 다시 시도해주세요.');
      return;
    }
    const travelerKey = traveler.key;
    try {
      setPassportUploadingKey(travelerKey);
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      const originalName = file?.name || 'passport';
      const dotIndex = originalName.lastIndexOf('.');
      const ext = dotIndex >= 0 ? originalName.slice(dotIndex) : '';
      const baseName = dotIndex >= 0 ? originalName.slice(0, dotIndex) : originalName;
      const safeBaseName = baseName || 'passport';
      const fileName = `${timestamp}_${travelerNo}_${safeBaseName}${ext}`;
      const ocrRes = await uploadS3Ocr({
        file,
        fileName,
        filePath: 'passportcopy/1/',
        key: travelerNo,
        isUpload: true,
      });
      const uploadedUrl = String(ocrRes?.url || '').trim();
      if (!uploadedUrl) {
        throw new Error('업로드 결과 URL을 찾지 못했습니다.');
      }
      const originMarker = '/orgin/';
      const originIndex = uploadedUrl.indexOf(originMarker);
      const storedPath = originIndex >= 0 ? uploadedUrl.slice(originIndex + originMarker.length) : uploadedUrl;
      await saveTravelerPassportCopy({ saleNo, travelerNo, filePath: storedPath });

      // 1차: 예약관리 여행객리스트 기준 데이터
      const updated = await getReservationTravelerListBySaleNo({ saleNo });
      const withKeys = (Array.isArray(updated) ? updated : []).map((r, idx) => ({
        ...r,
        key: String(r?.여행객번호 || r?.여행객키 || idx),
      }));

      // 2차: RCT_S_판매번호별고객리스트에서 내려오는 여권사본경로를 병합
      let merged = withKeys;
      try {
        const rctList = await getCustomerListBySaleNoFromRCT({ saleNo });
        const byTravelerNo = new Map();
        (Array.isArray(rctList) ? rctList : []).forEach((row) => {
          const no = Number(row?.여행객번호);
          if (Number.isFinite(no) && no > 0) {
            byTravelerNo.set(no, row);
          }
        });
        merged = withKeys.map((row) => {
          const no = Number(row?.여행객번호 || row?.여행객키);
          const rctRow = Number.isFinite(no) && no > 0 ? byTravelerNo.get(no) : null;
          if (rctRow && rctRow.여권사본경로) {
            return { ...row, 여권사본경로: rctRow.여권사본경로 };
          }
          return row;
        });
        try { console.debug('[ReservationDetail] RCT_S_판매번호별고객리스트', rctList); } catch { }
      } catch (e) {
        try { console.warn('[ReservationDetail] failed to load RCT_S_판매번호별고객리스트', e); } catch { }
      }

      setTravelers(merged);
      message.success('여권사본이 저장되었습니다.');

      const ocrObj = ocrRes?.ocr;
      const patch = normalizeOcrToTravelerPatch(ocrObj);
      if (ocrObj && Object.keys(patch).length > 0) {
        const applyDefault = {};
        Object.keys(patch).forEach((k) => {
          applyDefault[k] = true;
        });
        setPassportOcrTargetKey(travelerKey);
        setPassportOcrRaw(ocrRes || null);
        setPassportOcrPatch(patch);
        setPassportOcrApply(applyDefault);
        setPassportOcrModalOpen(true);
      } else {
        message.warning('OCR 결과를 찾지 못했습니다.');
      }
    } catch (e) {
      console.error(e);
      message.error(e?.message || '여권사본 저장에 실패했습니다.');
    } finally {
      setPassportUploadingKey(null);
    }
  };

  const handleDeletePassport = (traveler) => {
    if (!saleNo) {
      message.error('판매번호가 없어 여권사본을 삭제할 수 없습니다.');
      return;
    }
    const travelerNo = Number(traveler?.여행객번호 || traveler?.여행객키);
    if (!Number.isFinite(travelerNo) || travelerNo <= 0) {
      message.error('저장된 여행객에 대해서만 여권사본을 삭제할 수 있습니다.');
      return;
    }

    AntModal.confirm({
      title: '여권사본 삭제',
      content: '첨부된 여권사본을 삭제하시겠습니까?',
      okText: '삭제',
      cancelText: '취소',
      okButtonProps: { danger: true },
      async onOk() {
        const travelerKey = traveler.key;
        try {
          setPassportUploadingKey(travelerKey);
          await saveTravelerPassportCopy({ saleNo, travelerNo, filePath: '' });

          const updated = await getReservationTravelerListBySaleNo({ saleNo });
          const withKeys = (Array.isArray(updated) ? updated : []).map((r, idx) => ({
            ...r,
            key: String(r?.여행객번호 || r?.여행객키 || idx),
          }));

          let merged = withKeys;
          try {
            const rctList = await getCustomerListBySaleNoFromRCT({ saleNo });
            const byTravelerNo = new Map();
            (Array.isArray(rctList) ? rctList : []).forEach((row) => {
              const no = Number(row?.여행객번호);
              if (Number.isFinite(no) && no > 0) {
                byTravelerNo.set(no, row);
              }
            });
            merged = withKeys.map((row) => {
              const no = Number(row?.여행객번호 || row?.여행객키);
              const rctRow = Number.isFinite(no) && no > 0 ? byTravelerNo.get(no) : null;
              if (rctRow && rctRow.여권사본경로) {
                return { ...row, 여권사본경로: rctRow.여권사본경로 };
              }
              return { ...row, 여권사본경로: '' };
            });
            try { console.debug('[ReservationDetail] RCT_S_판매번호별고객리스트 after delete', rctList); } catch { }
          } catch (e) {
            try { console.warn('[ReservationDetail] failed to load RCT_S_판매번호별고객리스트 after delete', e); } catch { }
          }

          setTravelers(merged);
          message.success('여권사본이 삭제되었습니다.');
        } catch (e) {
          console.error(e);
          message.error(e?.message || '여권사본 삭제에 실패했습니다.');
        } finally {
          setPassportUploadingKey(null);
        }
      },
    });
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getOrderInfo({ orderNo });
        if (!mounted) return;
        setInfo(data || null);
        // Debug: verify what fields exist for saleNo detection
        try { console.debug('[ReservationDetail] getOrderInfo', { orderNo, data }); } catch { }
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || '주문 정보를 불러오지 못했습니다.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [orderNo]);

  // Fetch sale status & cancel flag by orderNo
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!orderNo) return;
      try {
        setStatusLoading(true);
        setStatusError(null);
        const row = await getOrderSaleStatus({ orderNo });
        if (!mounted) return;
        setSaleStatusRow(row || null);
        const cancelVal = (row?.취소여부 ?? null);
        setCancelLocal(cancelVal == null ? null : String(cancelVal));
        try { console.debug('[ReservationDetail] sale status', row); } catch { }
      } catch (e) {
        if (!mounted) return;
        setStatusError(e?.message || '예약 상태를 불러오지 못했습니다.');
      } finally {
        if (mounted) setStatusLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [orderNo]);

  const displayItems = useMemo(() => {
    if (!info) return [];
    return [
      // 고객결제가/미수금은 상단 정보 카드에서 제외
      // 주문경로/주문일은 주문번호 배지 말풍선에서 표시하므로 제거
    ];
  }, [info]);

  // Prefer saleNo from navigation state (passed from ReservationManagement),
  // fallback to URL query params (for new tab opens), then to fields from order info.
  const saleNo = useMemo(() => {
    const fromNav = location?.state?.saleNo;
    if (fromNav != null) return fromNav;
    // Check URL query params
    const searchParams = new URLSearchParams(location?.search);
    const fromQuery = searchParams.get('saleNo');
    if (fromQuery != null) return fromQuery;
    if (!info) return null;
    return (info?.판매번호 ?? info?.주문판매번호 ?? null);
  }, [location?.state?.saleNo, location?.search, info]);

  const currentUserNo = useMemo(() => {
    const { userNo } = getUserInfo() || {};
    return userNo != null ? String(userNo) : '';
  }, []);

  const getLogAuthorUserNo = (row) => {
    const candidates = [row?.유저번호, row?.작성자유저번호, row?.작성자번호, row?.등록자유저번호, row?.등록자번호];
    const v = candidates.find((x) => x != null && String(x).trim() !== '');
    return v != null ? String(v) : '';
  };

  const getLogReferenceNo = (row) => {
    const candidates = [row?.참조번호, row?.기록번호, row?.예약기록번호, row?.로그번호, row?.seq, row?.SEQ];
    const v = candidates.find((x) => x != null && String(x).trim() !== '');
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const loadReservationLogs = async ({ silent = false } = {}) => {
    if (!saleNo) return;
    if (!silent) {
      setLogsLoading(true);
      setLogsError(null);
    }
    try {
      const rows = await getReservationLogsBySaleNo({ saleNo });
      setReservationLogs(Array.isArray(rows) ? rows : []);
    } catch (e) {
      if (!silent) setLogsError(e?.message || '예약기록 조회에 실패했습니다.');
    } finally {
      if (!silent) setLogsLoading(false);
    }
  };

  useEffect(() => {
    try { console.debug('[ReservationDetail] computed saleNo', { saleNo, info }); } catch { }
  }, [saleNo, info]);

  // Preload reservation logs (예약기록) when entering the page so we can display 업무메모 separately.
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!saleNo) return;
      try {
        setLogsLoading(true);
        setLogsError(null);
        const rows = await getReservationLogsBySaleNo({ saleNo });
        if (!mounted) return;
        setReservationLogs(Array.isArray(rows) ? rows : []);
      } catch (e) {
        if (!mounted) return;
        setLogsError(e?.message || '예약기록 조회에 실패했습니다.');
      } finally {
        if (!mounted) return;
        setLogsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [saleNo]);

  // Resolve product ID by saleNo when not available from info/navigation
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!saleNo) return;
      if (baseProductId) return; // already have product id
      try {
        const row = await getProductIdBySaleNo({ saleNo });
        if (!mounted) return;
        const pid = row?.상품번호 ?? row?.productId ?? null;
        setResolvedProductId(pid ? Number(pid) : null);
        try { console.debug('[ReservationDetail] resolved product id by saleNo', { saleNo, row, pid }); } catch { }
      } catch (e) {
        try { console.error('[ReservationDetail] failed to resolve product id', e); } catch { }
      }
    })();
    return () => { mounted = false; };
  }, [saleNo, baseProductId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!invoiceOpen) return;
      if (!saleNo) {
        setInvoiceDepositSummary(null);
        return;
      }
      try {
        const depositRes = await getSaleDepositList({ saleNo });
        if (!mounted) return;
        setInvoiceDepositSummary(depositRes?.summary || null);
      } catch (e) {
        if (!mounted) return;
        setInvoiceDepositSummary(null);
      }
    })();
    return () => { mounted = false; };
  }, [invoiceOpen, saleNo]);

  // Initialize new receipt form when showing
  useEffect(() => {
    if (showAddReceiptForm) {
      resetNewReceiptForm();
    }
  }, [showAddReceiptForm]);

  // Fetch price info when saleNo is available
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (saleNo == null) return;
      try {
        setPriceLoading(true);
        setPriceError(null);
        const data = await getReservationPriceInfo({ saleNo });
        if (!mounted) return;
        setPriceInfo(data || null);
        // initialize cancel fee draft from fetched data
        try {
          const fee = Number(data?.취소료원화) || 0;
          setCancelFeeDraft(fee);
        } catch { }
        try { console.debug('[ReservationDetail] priceInfo', data); } catch { }
      } catch (e) {
        if (!mounted) return;
        setPriceError(e?.message || '가격 정보를 불러오지 못했습니다.');
      } finally {
        if (mounted) setPriceLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [saleNo]);

  // 취소료 단독 수정
  const handleCancelFeeSave = async () => {
    if (!saleNo) { message.error('판매번호가 없어 취소료를 저장할 수 없습니다.'); return; }
    try {
      setCancelFeeSaving(true);
      await updateCancelFee({ saleNo, cancelFee: cancelFeeDraft });
      message.success('취소료가 수정되었습니다.');
      // 최신 가격정보 재조회
      setPriceLoading(true);
      setPriceError(null);
      const latestPrice = await getReservationPriceInfo({ saleNo });
      setPriceInfo(latestPrice || null);
      setCancelFeeDraft(Number(latestPrice?.취소료원화) || 0);
      setCancelFeeEditing(false);
    } catch (e) {
      message.error(e?.message || '취소료를 저장하지 못했습니다.');
    } finally {
      setPriceLoading(false);
      setCancelFeeSaving(false);
    }
  };

  const openAgencyCommissionModal = () => {
    const p = priceInfo || {};
    const current = Number(p?.대리점커미션원화 ?? p?.대리점커미션 ?? p?.커미션원화 ?? 0) || 0;
    setAgencyCommissionDraft(current);
    setAgencyCommissionModalOpen(true);
  };

  const handleAgencyCommissionSave = async () => {
    if (!saleNo) { message.error('판매번호가 없어 대리점 커미션을 저장할 수 없습니다.'); return; }
    try {
      setAgencyCommissionSaving(true);
      await updateAgencyCommission({ saleNo, amount: agencyCommissionDraft });
      message.success('대리점 커미션이 수정되었습니다.');
      // 최신 가격정보 재조회
      setPriceLoading(true);
      setPriceError(null);
      const latestPrice = await getReservationPriceInfo({ saleNo });
      setPriceInfo(latestPrice || null);
      const nextCommission = Number(latestPrice?.대리점커미션원화 ?? latestPrice?.대리점커미션 ?? latestPrice?.커미션원화 ?? 0) || 0;
      setAgencyCommissionDraft(nextCommission);
      setAgencyCommissionModalOpen(false);
    } catch (e) {
      message.error(e?.message || '대리점 커미션을 저장하지 못했습니다.');
    } finally {
      setPriceLoading(false);
      setAgencyCommissionSaving(false);
    }
  };

  // Load 단체 전도금 내역 when saleNo available
  useEffect(() => {
    let active = true;
    (async () => {
      if (!saleNo) { setPreDepRows([]); setPreDepSummary(null); return; }
      try {
        setPreDepLoading(true);
        setPreDepError(null);
        const [{ list, summary }, cashbookRes] = await Promise.all([
          getPreDepositInfoBySaleNo({ saleNo }),
          getCashbookList().catch(() => ({ list: [], mapping: [] })),
        ]);
        if (!active) return;
        const cbAll = Array.isArray(cashbookRes?.list) ? cashbookRes.list : [];
        const cbMapping = Array.isArray(cashbookRes?.mapping) ? cashbookRes.mapping : [];
        setCashbookAllRows(cbAll);
        setCashbookMappingRows(cbMapping);
        const rows = Array.isArray(list) ? list : [];
        const enriched = rows.map((row) => {
          const detailNo = Number(row?.세부내역번호) || 0;
          if (!detailNo || !cbMapping.length || !cbAll.length) return row;
          const mappingRow = cbMapping.find((m) => {
            const mPreId = Number(m?.전도금번호 != null ? m.전도금번호 : m?.세부내역번호) || 0;
            return mPreId === detailNo;
          });
          if (!mappingRow) return row;
          const txNo = mappingRow?.거래일련번호;
          const cbRow = txNo ? cbAll.find((c) => String(c?.거래일련번호) === String(txNo)) : null;
          if (!cbRow) return row;
          return { ...row, 매핑출납일: cbRow.출납일 };
        });
        setPreDepRows(enriched);
        setPreDepSummary(summary || null);
      } catch (e) {
        if (!active) return;
        setPreDepRows([]);
        setPreDepSummary(null);
        setPreDepError(e?.message || '전도금 내역을 불러오지 못했습니다.');
      } finally {
        if (active) setPreDepLoading(false);
      }
    })();
    return () => { active = false; };
  }, [saleNo]);

  // Fetch extra services when saleNo is available
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!saleNo) { setExtraServices([]); return; }
      try {
        setExtraLoading(true);
        setExtraError(null);
        const list = await getReservationExtraServices({ saleNo });
        if (!mounted) return;
        setExtraServices(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!mounted) return;
        setExtraError(e?.message || '추가 서비스 정보를 불러오지 못했습니다.');
      } finally {
        if (mounted) setExtraLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [saleNo]);

  // Fetch 고객 요청사항 by 판매번호
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!saleNo) { setRequestItems([]); return; }
      try {
        setRequestLoading(true);
        setRequestError(null);
        const list = await getSaleCustomerRequestItems({ saleNo });
        if (!mounted) return;
        const normalized = Array.isArray(list)
          ? list
            .map((it) => ({
              title: it?.구분 ?? it?.title ?? '',
              content: it?.내용 ?? it?.content ?? '',
            }))
            .filter((it) => (String(it.title).trim() !== '' || String(it.content).trim() !== ''))
          : [];
        setRequestItems(normalized);
      } catch (e) {
        if (!mounted) return;
        setRequestError(e?.message || '요청사항을 불러오지 못했습니다.');
      } finally {
        if (mounted) setRequestLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [saleNo]);

  // Common departure date across travelers (동일 값 가정)
  const saleDepartureDate = useMemo(() => {
    const list = Array.isArray(travelers) ? travelers : [];
    const found = list.find((t) => t?.판매출발일);
    return found?.판매출발일 ?? null;
  }, [travelers]);

  // Fetch traveler list when saleNo is available
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (saleNo == null) return;
      // Initialize edit lock from server sale info (명단잠금여부)
      try {
        const row = await getReservationSaleInfo({ saleNo });
        if (!mounted) return;
        if (row && typeof row.명단잠금여부 !== 'undefined') {
          setEditLocked(!!row.명단잠금여부);
        }
        setSaleInfoRow(row || null);
      } catch (e) {
        try { console.warn('[ReservationDetail] failed to load sale info for edit lock', e); } catch { }
      }
      try {
        setTravelerLoading(true);
        setTravelerError(null);
        const list = await getReservationTravelerListBySaleNo({ saleNo });
        if (!mounted) return;
        // Ensure unique keys
        const baseWithKeys = (Array.isArray(list) ? list : []).map((r, idx) => ({ ...r, key: String(r?.여행객키 || r?.여행객번호 || idx) }));

        // Merge passport copy URL from RCT_S_판매번호별고객리스트 when available
        let merged = baseWithKeys;
        try {
          const rctList = await getCustomerListBySaleNoFromRCT({ saleNo });
          const byTravelerNo = new Map();
          (Array.isArray(rctList) ? rctList : []).forEach((row) => {
            const no = Number(row?.여행객번호);
            if (Number.isFinite(no) && no > 0) {
              byTravelerNo.set(no, row);
            }
          });
          merged = baseWithKeys.map((row) => {
            const no = Number(row?.여행객번호 || row?.여행객키);
            const rctRow = Number.isFinite(no) && no > 0 ? byTravelerNo.get(no) : null;
            if (rctRow && rctRow.여권사본경로) {
              return { ...row, 여권사본경로: rctRow.여권사본경로 };
            }
            return row;
          });
          try { console.debug('[ReservationDetail] initial RCT_S_판매번호별고객리스트', rctList); } catch { }
        } catch (mergeErr) {
          try { console.warn('[ReservationDetail] failed to load initial RCT_S_판매번호별고객리스트', mergeErr); } catch { }
        }

        setTravelers(merged);
        // Load and prune locally remembered newly-added ids so only existing ones remain
        const stored = loadNewlyAddedIds(saleNo);
        const idSet = new Set(merged.map((r) => String(r?.여행객번호 || r?.여행객키 || '')));
        const pruned = new Set(Array.from(stored).filter((id) => idSet.has(String(id))));
        setNewlyAddedIds(pruned);
      } catch (e) {
        if (!mounted) return;
        setTravelerError(e?.message || '여행객 정보를 불러오지 못했습니다.');
      } finally {
        if (mounted) setTravelerLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [saleNo]);

  // Group info is now handled inside GroupInfoDrawer
  return (
    <ConfigProvider locale={koKR}>
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <AntTooltip title={productIdForSettings ? '상세페이지 설정으로 이동' : '상품 ID가 없어 이동할 수 없습니다.'}>
                <h2
                  className={`text-xl font-semibold ${productIdForSettings ? 'text-gray-800 cursor-pointer hover:text-gray-500 hover:opacity-80 transition-colors duration-150 mb-1' : 'text-gray-800'}`}
                  onClick={productIdForSettings ? goToDetailSettings : undefined}
                  role={productIdForSettings ? 'button' : undefined}
                  tabIndex={productIdForSettings ? 0 : -1}
                  onKeyDown={(e) => {
                    if (!productIdForSettings) return;
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToDetailSettings(); }
                  }}
                >
                  {productName}
                </h2>
              </AntTooltip>
              {info?.주문번호 && (
                <AntTooltip
                  placement="bottomLeft"
                  trigger={["hover", "click"]}
                  overlayInnerStyle={{
                    background: '#ffffff',
                    color: '#111827',
                    padding: 10,
                    borderRadius: 8,
                    boxShadow: '0 6px 18px rgba(0,0,0,0.15)',
                    minWidth: 200,
                  }}
                  title={(
                    <div className="text-xs space-y-1.5">
                      {info?.주문경로 && (
                        <div className="flex items-center">
                          <span className="text-gray-500 w-14">주문경로</span>
                          <span className="text-gray-800">{info?.주문경로}</span>
                        </div>
                      )}
                      {info?.주문일 && (
                        <div className="flex items-center">
                          <span className="text-gray-500 w-14">주문일</span>
                          <span className="text-gray-800">{info?.주문일}</span>
                        </div>
                      )}
                      {info?.사이트 && (
                        <div className="flex items-center">
                          <span className="text-gray-500 w-14">사이트</span>
                          <span className="text-gray-800">{info?.사이트}</span>
                        </div>
                      )}
                    </div>
                  )}
                >
                  <div className="text-xs px-2 text-gray-600 hover:border-blue-300 hover:text-blue-600">
                    주문번호: {info?.주문번호} <CalendarOutlined className="text-gray-500" />
                    <span className="inline-flex items-center h-7 px-2 rounded text-blue-600">행사기간: {saleInfoRow?.행사기간 || '-'}</span>
                  </div>
                </AntTooltip>
              )}
            </div>
            <div className="text-xs flex items-center gap-2">
              {saleInfoRow?.행사기간 && (
                <>
                  <AntButton
                    size="small"
                    loading={urlGenerating}
                    icon={<CopyOutlined />}
                    onClick={async () => {
                      if (!saleNo) { message.error('판매번호가 없어 URL을 생성할 수 없습니다.'); return; }
                      try {
                        setUrlGenerating(true);
                        const row = await getReservationDirectInputUrl({ saleNo });
                        const orderNoParam = row?.주문번호 ?? info?.주문번호;
                        const salesNoParam = row?.판매번호 ?? saleNo;
                        const orderKeyParam = row?.유니크키 ?? row?.orderKey;
                        if (!orderNoParam || !salesNoParam || !orderKeyParam) {
                          throw new Error('필요한 값(주문번호/판매번호/키)을 가져오지 못했습니다.');
                        }
                        const url = `https://campaigncustomer.tripbox.co.kr/?orderNo=${encodeURIComponent(orderNoParam)}&salesNo=${encodeURIComponent(salesNoParam)}&orderKey=${encodeURIComponent(orderKeyParam)}`;
                        setLastGeneratedUrl(url);
                        try {
                          if (navigator?.clipboard?.writeText) {
                            await navigator.clipboard.writeText(url);
                          } else {
                            const ta = document.createElement('textarea');
                            ta.value = url;
                            ta.style.position = 'fixed';
                            ta.style.opacity = '0';
                            document.body.appendChild(ta);
                            ta.focus();
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                          }
                          message.success('URL이 복사되었습니다.');
                          AntModal.success({
                            title: '예약정보 입력요청 URL',
                            content: (
                              <div>
                                <div className="text-sm text-gray-700 break-all">{url}</div>
                              </div>
                            ),
                          });
                        } catch (copyErr) {
                          message.warning('클립보드 복사에 실패했습니다. 아래 URL을 직접 복사하세요.');
                          AntModal.info({
                            title: '예약정보 입력요청 URL',
                            content: (
                              <div>
                                <div className="text-sm text-gray-700 break-all">{url}</div>
                              </div>
                            ),
                          });
                        }
                      } catch (e) {
                        console.error(e);
                        message.error(e?.message || 'URL 생성에 실패했습니다.');
                      } finally {
                        setUrlGenerating(false);
                      }
                    }}
                  >
                    예약정보 입력요청URL 생성
                  </AntButton>
                </>
              )}
              {/* 판매상태 표시 및 변경 (표시 버튼 자체가 드롭다운 트리거) */}
              <AntDropdown
                menu={{
                  items: (
                    cancelLocal === '1'
                      ? [
                        { key: '취소접수', label: '취소접수' },
                        { key: '취소확정', label: '취소확정' },
                        { key: '취소완료', label: '취소완료' },
                      ]
                      : [
                        { key: '접수', label: '예약접수' },
                        { key: '확정', label: '예약확정' },
                        { key: '불가', label: '예약불가' },
                      ]
                  ),
                  onClick: async ({ key }) => {
                    if (!saleNo) { message.error('판매번호가 없어 상태를 변경할 수 없습니다.'); return; }
                    try {
                      setStatusSaving(true);
                      const hide = message.loading('판매상태 변경 중...', 0);
                      if (cancelLocal === '1') {
                        // 취소 상태 단계 변경
                        // level1='049021', level2/level3 per selection
                        const level1 = '049021';
                        const level2 = (key === '취소확정' || key === '취소완료') ? '049022' : '';
                        const level3 = (key === '취소완료') ? '049029' : '';
                        await updateSaleStatusAdvanced({ saleNo, level1, level2, level3 });
                      } else {
                        // 일반 상태 변경 (예약)
                        const level2 = key === '확정' ? '049011' : key === '불가' ? '049031' : '';
                        await updateSaleStatus({ saleNo, level2 });
                      }
                      hide();
                      message.success('판매상태가 변경되었습니다.');
                      // Refresh status row and (optionally) cancel flag from server
                      try {
                        setStatusLoading(true);
                        const row = await getOrderSaleStatus({ orderNo });
                        setSaleStatusRow(row || null);
                        const cancelVal = (row?.취소여부 ?? cancelLocal);
                        setCancelLocal(cancelVal == null ? null : String(cancelVal));
                      } finally {
                        setStatusLoading(false);
                      }
                    } catch (e) {
                      message.error(e?.message || '상태 변경에 실패했습니다.');
                    } finally {
                      setStatusSaving(false);
                    }
                  }
                }}
                placement="bottomLeft"
                trigger={["click"]}
              >
                <AntButton size="small" className={`!h-7 !text-xs border ${statusBadgeClass}`} loading={statusSaving || statusLoading}>
                  {saleStatusRow?.판매상태 || '예약상태'}
                </AntButton>
              </AntDropdown>
              {/* 예약 상태 토글 (compact) */}
              {!statusLoading && (
                <AntButton
                  size="small"
                  onClick={async () => {
                    if (cancelLocal == null) return;
                    if (cancelLocal === '0') {
                      // Open modal to input cancel fee
                      if (!saleNo) { message.error('판매번호가 없어 취소 처리를 할 수 없습니다.'); return; }
                      setCancelModalOpen(true);
                      setCancelInfoLoading(true);
                      setCancelInfoError(null);
                      try {
                        const row = await getCancelInfo({ saleNo });
                        setCancelInfoRow(row || null);
                        const fee = Number(row?.취소료) || 0;
                        setCancelFee(fee);
                      } catch (e) {
                        setCancelInfoError(e?.message || '취소 정보를 불러오지 못했습니다.');
                      } finally {
                        setCancelInfoLoading(false);
                      }
                    } else {
                      // Restore with server call
                      AntModal.confirm({
                        title: '예약복원',
                        content: '예약을 복원하시겠습니까?',
                        okText: '복원',
                        cancelText: '취소',
                        onOk: async () => {
                          if (!saleNo) { message.error('판매번호가 없어 복원할 수 없습니다.'); return; }
                          const hide = message.loading('예약 복원 중...', 0);
                          try {
                            await restoreReservation({ saleNo });
                            message.success('예약이 복원되었습니다.');
                            setCancelLocal('0');
                            // Optionally refresh status from server
                            try {
                              setStatusLoading(true);
                              const row = await getOrderSaleStatus({ orderNo });
                              setSaleStatusRow(row || null);
                            } finally {
                              setStatusLoading(false);
                            }
                            // Refresh price info immediately
                            try {
                              setPriceLoading(true);
                              setPriceError(null);
                              const latestPrice = await getReservationPriceInfo({ saleNo });
                              setPriceInfo(latestPrice || null);
                              setCancelFeeDraft(Number(latestPrice?.취소료원화) || 0);
                            } catch (e2) {
                              setPriceError(e2?.message || '가격 정보를 불러오지 못했습니다.');
                            } finally {
                              setPriceLoading(false);
                            }
                          } catch (e) {
                            message.error(e?.message || '복원 처리에 실패했습니다.');
                          } finally {
                            hide();
                          }
                        },
                      });
                    }
                  }}
                  disabled={cancelLocal == null}
                  className="!h-7 !text-xs text-red-400"
                >
                  {cancelLocal === '1' ? '예약복원 하기' : '예약취소 하기'}
                </AntButton>
              )}
              {statusLoading && <span className="text-gray-400">상태 불러오는 중...</span>}
            </div>
          </div>
          <div className="space-x-2">
            <button className="px-3 h-7 text-sm rounded border border-gray-200 hover:bg-gray-50" onClick={() => navigate(-1)}>뒤로</button>
            <button
              className="px-3 h-7 text-sm rounded border border-gray-200 hover:bg-gray-50"
              
              onClick={() => setInvoiceOpen(true)}
            >
              인보이스
            </button>

            <button
              className="px-3 h-7 text-sm rounded border border-gray-200 hover:bg-gray-50"
              onClick={handleExportTravelersExcel}
            >
              엑셀로 내보내기
            </button>

            <button
              className="backdrop-blur-sm bg-slate-900/95 text-white px-3 h-7 text-sm rounded border border-gray-200 hover:bg-gray-600"
              
              onClick={() => {
                if (!saleNo) {
                  message.error('판매번호가 없어 단체정보를 열 수 없습니다.');
                  return;
                }
                setGroupOpen(true);
              }}
            >
              단체정보 상세보기
            </button>
          </div>
        </div>

        {loading && <div className="text-gray-500">불러오는 중...</div>}
        {!loading && error && <div className="text-red-600">{error}</div>}

        {/* 2x2 grid layout: 좌측(주문자/여행객), 우측(가격정보/업무메모) */}
        {!loading && !error && (
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-x-4 gap-y-2">
            <div className="col-span-1 lg:col-span-7 flex flex-col gap-y-2">
              <div className="bg-white border-b border-gray-200 py-4">
                <div className="flex items-center justify-between mb-3">
                  
                  <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                    <span className="inline-block w-1 h-4 bg-blue-600 rounded-sm" />
                    주문자 정보
                  </h3>
                  {!ordererEditing ? (
                    <AntButton
                      size="small"
                      onClick={() => {
                        setOrdererName(info?.주문자 || '');
                        setOrdererPhone(info?.주문자휴대폰 || '');
                        setOrdererEmail(info?.주문자이메일 || '');
                        setOrderAgencyNo(String(info?.판매업체번호 ?? info?.대리점번호 ?? info?.대리점코드 ?? ''));
                        setOrderAgencyName(String(info?.판매업체명 ?? info?.대리점명 ?? info?.대리점 ?? ''));
                        setOrdererEditing(true);
                      }}
                    >
                      편집
                    </AntButton>
                  ) : (
                    <div className="flex gap-2">
                      <AntButton size="small" onClick={() => setOrdererEditing(false)}>취소</AntButton>
                      <AntButton
                        type="primary"
                        size="small"
                        loading={ordererSaving}
                        onClick={async () => {
                          try {
                            setOrdererSaving(true);
                            const hide = message.loading('주문자 정보 저장 중...', 0);
                            const tasks = [
                              updateOrdererInfo({
                                orderNo,
                                ordererName,
                                ordererPhone,
                                ordererEmail,
                              }),
                            ];

                            const trimmedAgencyNo = String(orderAgencyNo ?? '').trim();
                            const trimmedAgencyName = String(orderAgencyName ?? '').trim();
                            if (trimmedAgencyNo) {
                              tasks.push(updateOrderAgency({
                                orderNo,
                                agencyNo: Number(trimmedAgencyNo),
                                agencyName: trimmedAgencyName,
                              }));
                            } else if (trimmedAgencyName) {
                              hide();
                              message.error('대리점번호를 입력하세요.');
                              return;
                            }

                            await Promise.all(tasks);
                            hide();
                            message.success('저장되었습니다.');
                            setOrdererEditing(false);
                            // refresh info
                            setStatusLoading(true);
                            try {
                              const data = await getOrderInfo({ orderNo });
                              setInfo(data || null);
                            } finally {
                              setStatusLoading(false);
                            }
                          } catch (e) {
                            message.error(e?.message || '저장에 실패했습니다.');
                          } finally {
                            setOrdererSaving(false);
                          }
                        }}
                      >
                        저장
                      </AntButton>
                    </div>
                  )}
                </div>
                {!ordererEditing && (
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-sm text-gray-800">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-600 px-1.5 py-0.5 min-w-[40px] text-center whitespace-nowrap">이름</span>
                      <span className="break-all font-medium">{info?.주문자 ?? '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-600 px-1.5 py-0.5 min-w-[40px] text-center whitespace-nowrap">휴대폰</span>
                      <span className="break-all font-medium">{info?.주문자휴대폰 ?? '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-600 px-1.5 py-0.5 min-w-[40px] text-center whitespace-nowrap">이메일</span>
                      <span className="break-all font-medium">{info?.주문자이메일 ?? '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-600 px-1.5 py-0.5 min-w-[40px] text-center whitespace-nowrap">대리점</span>
                      <span className="break-all font-medium">{info?.대리점 ?? '-'}</span>
                    </div>
                  </div>
                )}
                {ordererEditing && (
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-sm text-gray-800">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-600 px-1.5 py-0.5 min-w-[40px] text-center whitespace-nowrap">이름</span>
                      <AntInput value={ordererName} onChange={(e) => setOrdererName(e.target.value)} />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-600 px-1.5 py-0.5 min-w-[40px] text-center whitespace-nowrap">휴대폰</span>
                      <AntInput value={ordererPhone} onChange={(e) => setOrdererPhone(e.target.value)} />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-600 px-1.5 py-0.5 min-w-[40px] text-center whitespace-nowrap">이메일</span>
                      <AntInput value={ordererEmail} onChange={(e) => setOrdererEmail(e.target.value)} />
                    </div>
                    {/* Agency number field hidden */}
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-600 px-1.5 py-0.5 min-w-[40px] text-center whitespace-nowrap">대리점</span>
                      <AntInput
                        value={orderAgencyName}
                        onChange={(e) => setOrderAgencyName(e.target.value)}
                        placeholder="대리점명"
                        readOnly
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (ordererSaving) return;
                          openAgencySelectModal();
                        }}
                        suffix={(
                          <SearchOutlined
                            style={{ color: '#64748b', cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (ordererSaving) return;
                              openAgencySelectModal();
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                )}

                <AntModal
                  open={agencySelectOpen}
                  onCancel={() => {
                    if (agencySelectLoading) return;
                    setAgencySelectOpen(false);
                  }}
                  footer={null}
                  centered
                  maskClosable={!agencySelectLoading}
                  width={820}
                  title="거래처 선택"
                >
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <AntInput
                        placeholder="거래처명/코드/담당자/전화 검색"
                        value={agencySelectQuery}
                        onChange={(e) => setAgencySelectQuery(e.target.value)}
                        disabled={agencySelectLoading}
                      />
                      <AntButton
                        onClick={() => {
                          if (agencySelectLoading) return;
                          openAgencySelectModal();
                        }}
                        loading={agencySelectLoading}
                      >
                        새로고침
                      </AntButton>
                    </div>

                    {agencySelectError && (
                      <div className="text-sm text-red-600">{agencySelectError}</div>
                    )}

                    <Table
                      size="small"
                      rowKey={(row, idx) => String(row?.코드 ?? row?.업체번호2 ?? row?.거래처번호 ?? idx)}
                      loading={agencySelectLoading}
                      dataSource={filteredAgencySelectList}
                      pagination={false}
                      scroll={{ y: 400 }}
                      columns={[
                        {
                          title: '코드',
                          dataIndex: '코드',
                          width: 110,
                          render: (_, row) => String(row?.코드 ?? row?.업체번호2 ?? row?.거래처번호 ?? ''),
                        },
                        {
                          title: '거래처명',
                          dataIndex: '코드명',
                          render: (_, row) => String(row?.코드명 ?? row?.거래처명 ?? ''),
                        },
                        {
                          title: '담당자',
                          width: 140,
                          render: (_, row) => String(row?.담당자명 ?? row?.담당자 ?? ''),
                        },
                        {
                          title: '전화',
                          width: 140,
                          render: (_, row) => String(row?.대표전화 ?? row?.전화번호 ?? ''),
                        },
                        {
                          title: '선택',
                          width: 80,
                          render: (_, row) => (
                            <AntButton
                              size="small"
                              type="primary"
                              onClick={() => {
                                const code = row?.코드 ?? row?.업체번호2 ?? row?.거래처번호;
                                const name = row?.코드명 ?? row?.거래처명;
                                if (code === null || code === undefined || String(code).trim() === '') {
                                  message.error('선택한 거래처의 코드가 없습니다.');
                                  return;
                                }
                                setOrderAgencyNo(String(code));
                                setOrderAgencyName(String(name ?? ''));
                                setAgencySelectOpen(false);
                              }}
                            >
                              선택
                            </AntButton>
                          ),
                        },
                      ]}
                    />
                  </div>
                </AntModal>
              </div>

              <div className="py-2">
                <div className="pt-5 sticky top-0 z-20 flex items-center justify-between mb-3 bg-white/95 backdrop-blur">
                  <div className="flex items-center gap-3">
                    <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                      <span className="inline-block w-1 h-4 bg-blue-600 rounded-sm" />
                      여행객 리스트
                    </h3>
                    {!editLocked && travelers.length > 0 && (
                      <AntCheckbox
                        indeterminate={selectedTravelerKeys.length > 0 && selectedTravelerKeys.length < travelers.length}
                        checked={travelers.length > 0 && selectedTravelerKeys.length === travelers.length}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            setSelectedTravelerKeys(travelers.map((t) => t.key));
                          } else {
                            setSelectedTravelerKeys([]);
                          }
                        }}
                      >
                        {travelers.length > 0 && selectedTravelerKeys.length === travelers.length ? '전체 해제' : '전체 선택'}
                      </AntCheckbox>
                    )}

                    {selectedTravelerKeys.length > 0 && (
                      <AntButton
                        size="small"
                        onClick={() => setBulkEditModalOpen(true)}
                      >
                        일괄수정
                      </AntButton>
                    )}
                    <AntButton
                      size="small"
                      onClick={() => setRoomModalOpen(true)}
                      className="border-purple-300 text-purple-600 hover:border-purple-400 hover:text-purple-700"
                    >
                      루밍 설정
                    </AntButton>
                  </div>
                  {travelerLoading && <span className="text-xs text-gray-500">불러오는 중...</span>}
                  {!travelerLoading && travelerError && <span className="text-xs text-red-600">{travelerError}</span>}
                  <div className="ml-auto flex items-center gap-2">
                    <AntButton
                      size="small"
                      onClick={async () => {
                        if (!saleNo) { message.error('판매번호가 없어 예약기록을 조회할 수 없습니다.'); return; }
                        setLogsModalOpen(true);
                        if ((reservationLogs || []).length === 0 && !logsLoading) {
                          await loadReservationLogs();
                        }
                      }}
                    >
                      예약기록
                    </AntButton>
                    <AntCheckbox
                      checked={editLocked}
                      onChange={async (e) => {
                        if (!saleNo) { message.error('판매번호가 없어 설정을 저장할 수 없습니다.'); return; }
                        const checked = e.target.checked;
                        const hide = message.loading('설정 저장 중...', 0);
                        try {
                          const prev = editLocked;
                          setEditLocked(checked);
                          await toggleTravelerEditLock({ saleNo, locked: checked });
                          message.success('설정이 저장되었습니다.');
                        } catch (err) {
                          console.error(err);
                          message.error(err?.message || '설정 저장에 실패했습니다.');
                          setEditLocked((prev) => !checked);
                        } finally {
                          hide();
                        }
                      }}
                    >
                      명단편집 불가
                    </AntCheckbox>

                    <AntButton
                      type="primary"
                      size="small"
                      loading={saving}
                      onClick={() => handleSave()}
                    >
                      저장
                    </AntButton>

                  </div>
                </div>

                <div className="space-y-2 mt-4">
                  {travelers.map((traveler, index) => (
                    <TravelerAccordionCard
                      key={traveler.key}
                      traveler={traveler}
                      index={index}
                      orderCancelled={isOrderCancelled}
                      isExpanded={expandedTravelers.has(traveler.key)}
                      onToggle={() => {
                        setExpandedTravelers((prev) => {
                          const next = new Set(prev);
                          if (next.has(traveler.key)) {
                            next.delete(traveler.key);
                          } else {
                            next.add(traveler.key);
                          }
                          return next;
                        });
                      }}
                      onUpdate={(updates) => {
                        setTravelers((prev) => prev.map((r) => (
                          r.key === traveler.key ? { ...r, ...updates } : r
                        )));
                      }}
                      onDelete={() => {
                        AntModal.confirm({
                          title: '명단 완전 삭제',
                          content: '이 여행객을 완전히 삭제하시겠습니까?',
                          okText: '삭제',
                          cancelText: '취소',
                          okButtonProps: { danger: true },
                          onOk: async () => {
                            try {
                              const next = travelers.filter((r) => r.key !== traveler.key);
                              setTravelers(next);
                              const entries = [];
                              next.forEach((t) => {
                                const travelerNo = Number(t?.여행객번호 || t?.여행객키);
                                if (!Number.isFinite(travelerNo) || travelerNo <= 0) return;
                                const age = t?.연령구분 || '성인';
                                const push = (fieldNo, val) => {
                                  if (val == null || val === '') return;
                                  entries.push({ 여행객번호: travelerNo, 필드번호: fieldNo, 필드값: String(val), 연령: String(age) });
                                };
                                push(8, t?.한글이름);
                                push(109, t?.성별);
                                push(9, t?.영문성);
                                push(10, t?.영문이름);
                              });
                              const adultCount = buildAdultCountFromTravelers(next);
                              const ageArray = buildAgeArrayFromTravelers(next);
                              const hide = message.loading('삭제 반영 중...', 0);
                              await saveReservationTravelersAfterDelete({
                                saleNo,
                                minimalTravelerRows: entries,
                                adultCount,
                                ageArray,
                              });
                              hide();
                              message.success('삭제가 반영되었습니다.');
                              const updated = await getReservationTravelerListBySaleNo({ saleNo });
                              const withKeys = (Array.isArray(updated) ? updated : []).map((r, idx) => ({ ...r, key: String(r?.여행객번호 || r?.여행객키 || idx) }));
                              setTravelers(withKeys);
                              const idsNow = new Set(withKeys.map((r) => String(r?.여행객번호 || r?.여행객키 || '')));
                              const nextAdded = new Set(Array.from(newlyAddedIds).filter((id) => idsNow.has(String(id))));
                              setNewlyAddedIds(nextAdded);
                              saveNewlyAddedIds(saleNo, nextAdded);

                              try {
                                const [latestPrice, latestInfo] = await Promise.all([
                                  getReservationPriceInfo({ saleNo }).catch(() => null),
                                  orderNo ? getOrderInfo({ orderNo }).catch(() => null) : Promise.resolve(null),
                                ]);
                                if (latestPrice) setPriceInfo(latestPrice);
                                if (latestInfo) setInfo(latestInfo);
                              } catch (e2) {
                                try { console.warn('[ReservationDetail] failed to refresh price/order info after delete traveler', e2); } catch { }
                              }

                              try {
                                if (paymentModalOpen) {
                                  await refreshPaymentModalData();
                                }
                              } catch (e3) {
                                try { console.warn('[ReservationDetail] failed to refresh payment modal after delete traveler', e3); } catch { }
                              }
                            } catch (e) {
                              console.error(e);
                              message.error(e?.message || '삭제 반영 중 오류가 발생했습니다.');
                            }
                          }
                        });
                      }}
                      onCancel={() => {
                        setTravelerCancelTargetKey(traveler.key);
                        setTravelerCancelAmount(Number(traveler?.상품금액원화) || 0);
                        setTravelerCancelModalOpen(true);
                      }}
                      onRestore={() => {
                        setTravelerRestoreTargetKey(traveler.key);
                        setTravelerRestorePrice(Number(traveler?.상품금액원화) || 0);
                        setTravelerRestoreTaxFuel(Number(traveler?.택스및유류원화) || 0);
                        setTravelerRestoreModalOpen(true);
                      }}
                      editLocked={editLocked}
                      isSelected={selectedTravelerKeys.includes(traveler.key)}
                      onSelect={(checked) => {
                        setSelectedTravelerKeys((prev) => {
                          if (checked) return Array.from(new Set([...prev, traveler.key]));
                          return prev.filter((k) => k !== traveler.key);
                        });
                      }}
                      passportUrl={traveler?.여권사본경로}
                      uploadingPassport={passportUploadingKey === traveler.key}
                      onUploadPassport={(file) => handlePassportUpload(traveler, file)}
                      onDeletePassport={() => handlePassportDelete(traveler)}
                      allowPassportUpload={isPassportUploadAllowed}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="col-span-1 lg:col-span-3 flex flex-col gap-y-2">
              <div className="px-4 max-h-[520px] overflow-auto">
                <div className="sticky top-0 z-20 flex items-center justify-between mb-3 bg-white/95 backdrop-blurborder-gray-100">
                  
                  {priceLoading && <span className="text-xs text-gray-500">불러오는 중...</span>}
                  {!priceLoading && priceError && <span className="text-xs text-red-600">{priceError}</span>}
                  {!priceLoading && !priceError && (
                    <div className="ml-auto">

                    </div>
                  )}
                </div>
                {!priceLoading && !priceError && (
                  <div className="space-y-4">
                    {(() => {
                      const p = priceInfo || {};
                      const cur = '';
                      const 상품금액 = Number(p?.상품금액원화) || 0;
                      const 텍스유류 = Number(p?.택스및유류원화) || 0;
                      const 취소료 = Number(p?.취소료원화) || 0;
                      const 프로모션할인 = Number(
                        p?.프로모션할인원화 ?? p?.프로모션할인 ?? p?.할인금액원화 ?? 0
                      ) || 0;
                      const 기타할인 = Number(
                        p?.기타할인원화 ?? p?.기타할인 ?? p?.기타할인금액원화 ?? p?.기타할인금액 ?? 0
                      ) || 0;
                      const 대리점커미션 = Number(
                        p?.대리점커미션원화 ?? p?.대리점커미션 ?? p?.커미션원화 ?? 0
                      ) || 0;
                      const 고객결제가 = Number(p?.고객결제가원화) || 0;
                      const 넷가 = Number(p?.넷가원화) || 0;
                      // 미수금 영역의 "고객 결제 금액"은 주문조회(주문정보) SP 응답의 결제금액을 사용
                      const 주문결제금액 = Number(info?.결제금액) || 0;
                      const 결제금액 = 주문결제금액;
                      const 미수금계산 = (Number(넷가) || 0) - (Number(결제금액) || 0);
                      const 추가서비스합계 = (Array.isArray(extraServices) ? extraServices : []).reduce((sum, r) => sum + (Number(r?.판매금액) || 0), 0);

                      const isCancelled =
                        (saleStatusRow?.취소여부 === 1) ||
                        String(saleStatusRow?.취소여부) === '1' ||
                        cancelLocal === '1';

                      // Unified KPI + Formula cards with inline actions
                      const CombinedCards = (
                        <div className="grid grid-cols-1 gap-4">
                          {/* 총 판매가 카드 */}
                          <div className="px-4 pb-4 pt-1 border-b border-gray-200">
                            <div className="text-base font-semibold text-gray-800 flex items-center gap-2">
                              <span className="inline-block w-1 h-4 bg-blue-600 rounded-sm" />
                              총 판매가
                            </div>
                            <div className="ml-3 text-2xl font-semibold text-gray-900 tabular-nums mt-1">{cur}{formatNumberWithCommas(고객결제가)}</div>
                            {isCancelled && (
                              <div className="ml-3 mt-2 text-[13px] text-gray-700 flex items-center gap-2 flex-wrap">
                                {!cancelFeeEditing ? (
                                  <>
                                    <span className="flex items-center gap-1">
                                      <span className="text-gray-500">취소료</span>
                                      <span className="tabular-nums font-semibold">{cur}{formatNumberWithCommas(취소료)}</span>
                                    </span>
                                    <AntButton
                                      size="small"
                                      type="link"
                                      className="!p-0 text-xs"
                                      onClick={() => {
                                        setCancelFeeDraft(Number(취소료) || 0);
                                        setCancelFeeEditing(true);
                                      }}
                                    >
                                      수정
                                    </AntButton>
                                  </>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <AntInputNumber
                                      size="small"
                                      min={0}
                                      value={cancelFeeDraft}
                                      onChange={(v) => setCancelFeeDraft(Number(v) || 0)}
                                      formatter={(val) => formatNumberWithCommas(typeof val === 'number' ? val : Number(val || 0))}
                                      parser={(val) => (val ? val.toString().replace(/,/g, '') : '')}
                                      style={{ width: 140 }}
                                    />
                                    <AntButton size="small" type="primary" loading={cancelFeeSaving} onClick={handleCancelFeeSave}>
                                      저장
                                    </AntButton>
                                    <AntButton
                                      size="small"
                                      onClick={() => {
                                        setCancelFeeEditing(false);
                                        setCancelFeeDraft(Number(취소료) || 0);
                                      }}
                                      disabled={cancelFeeSaving}
                                    >
                                      취소
                                    </AntButton>
                                  </div>
                                )}
                              </div>
                            )}
                            {!isCancelled && (
                              <div className="ml-3 mt-2 text-[13px] text-gray-700 flex items-center gap-2 flex-wrap">
                                <span className="flex items-center gap-1"><span className="text-gray-500">상품금액</span><span className="tabular-nums font-semibold">{cur}{formatNumberWithCommas(상품금액)}</span></span>
                                {추가서비스합계 > 0 && (
                                  <>
                                    <span className="text-gray-400">+</span>
                                    <button
                                      type="button"
                                      className="flex items-center gap-1 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400 rounded-sm"
                                      onClick={() => {
                                        if ((extraServices || []).length === 0) return;
                                        setExtraModalOpen(true);
                                      }}
                                    >
                                      <span className="text-gray-500 underline decoration-dotted underline-offset-2">추가 서비스(옵션)</span>
                                      <span className="tabular-nums font-semibold text-blue-700">{cur}{formatNumberWithCommas(추가서비스합계)}</span>
                                    </button>
                                  </>
                                )}
                                {텍스유류 > 0 && (
                                  <>
                                    <span className="text-gray-400">+</span>
                                    <span className="flex items-center gap-1"><span className="text-gray-500">추가판매가</span><span className="tabular-nums font-semibold">{cur}{formatNumberWithCommas(텍스유류)}</span></span>
                                  </>
                                )}
                                {프로모션할인 > 0 && (
                                  <>
                                    <span className="text-gray-400">-</span>
                                    <span className="flex items-center gap-1"><span className="text-gray-500">프로모션할인</span><span className="tabular-nums font-semibold">{cur}{formatNumberWithCommas(프로모션할인)}</span></span>
                                  </>
                                )}
                                {기타할인 > 0 && (
                                  <>
                                    <span className="text-gray-400">-</span>
                                    <span className="flex items-center gap-1"><span className="text-gray-500">기타할인</span><span className="tabular-nums font-semibold">{cur}{formatNumberWithCommas(기타할인)}</span></span>
                                  </>
                                )}
                                {대리점커미션 > 0 && (
                                  <>
                                    <span className="text-gray-400">-</span>
                                    <button
                                      type="button"
                                      className="flex items-center gap-1 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400 rounded-sm"
                                      onClick={() => {
                                        openAgencyCommissionModal();
                                      }}
                                    >
                                      <span className="text-gray-500 underline decoration-dotted underline-offset-2">대리점커미션</span>
                                      <span className="tabular-nums font-semibold text-blue-700">{cur}{formatNumberWithCommas(대리점커미션)}</span>
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          {/* 결제한 금액 카드 */}
                          <div
                            className="p-4 border-b border-gray-200"
                          >
                            <div className="flex items-center justify-between">
                              <div className="text-base font-semibold text-gray-800 flex items-center gap-2">
                                <span className="inline-block w-1 h-4 bg-blue-600 rounded-sm" />
                                입금된 금액
                              </div>
                              {isAdminUser && (
                                <AntButton
                                  size="small"
                                  type="default"
                                  className="text-[11px]"
                                  onClick={(e) => {
                                    openPaymentModal();
                                  }}
                                >
                                  입금액 편집
                                </AntButton>
                              )}
                            </div>
                            <div className="ml-3 text-2xl font-semibold text-gray-900 tabular-nums mt-1">{cur}{formatNumberWithCommas(결제금액)}</div>
                          </div>
                          {/* 미수금 카드 */}
                          <div className="p-4 border-b border-gray-200">
                            <div className="text-base font-semibold text-gray-800 flex items-center gap-2">
                              <span className="inline-block w-1 h-4 bg-blue-600 rounded-sm" />
                              미수금
                            </div>
                            <div className="ml-3 text-2xl font-semibold text-gray-900 tabular-nums mt-1">
                              {미수금계산 === 0 ? (
                                <span className="text-green-600">완납</span>
                              ) : (
                                <span>{cur}{formatNumberWithCommas(미수금계산)}</span>
                              )}
                            </div>
                            {미수금계산 !== 0 && (
                              <div className="ml-3 mt-2 text-[13px] text-gray-700 flex items-center gap-2 flex-wrap">
                                <span className="flex items-center gap-1"><span className="text-gray-500">넷가</span><span className="tabular-nums font-semibold">{cur}{formatNumberWithCommas(넷가)}</span></span>
                                <span className="text-gray-400">-</span>
                                <span className="flex items-center gap-1"><span className="text-gray-500">결제한 금액</span><span className="tabular-nums font-semibold">{cur}{formatNumberWithCommas(결제금액)}</span></span>
                              </div>
                            )}
                          </div>
                        </div>
                      );

                      // Extras
                      const Extras = ([Number(p?.환불금액) || 0, Number(p?.환불진행중금액) || 0].some(v => v > 0)) ? (
                        <div className="grid grid-cols-1 gap-4">
                          {[
                            { label: '환불금액', value: Number(p?.환불금액) || 0 },
                            { label: '환불진행', value: Number(p?.환불진행중금액) || 0 },
                          ].filter(x => x.value > 0).map((it) => (
                            <div key={it.label} className="flex flex-col">
                              <span className="text-xs text-gray-500 mb-1">{it.label}</span>
                              <span className="text-sm text-gray-800 tabular-nums">{cur}{formatNumberWithCommas(it.value)}</span>
                            </div>
                          ))}
                        </div>
                      ) : null;

                      return (
                        <>
                          {CombinedCards}
                          {Extras}
                          {/* 단체 전도금 섹션 (항상 표시) */}
                          <div className="mt-2">
                            <div className="px-4 pt-3 pb-6 border-b border-gray-200">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-base font-semibold text-gray-800 flex items-center gap-2">
                                    <span className="inline-block w-1 h-4 bg-blue-600 rounded-sm" />
                                    단체 전도금
                                  </span>
                                  {preDepSummary && (
                                    <span className="text-[11px] text-gray-600">
                                      총 전도금: {formatNumberWithCommas(preDepSummary?.행사전도금 || preDepSummary?.전도금합계 || 0)}원
                                    </span>
                                  )}
                                </div>
                                {isAdminUser && (
                                  <AntButton
                                    size="small"
                                    className="text-[11px]"

                                    onClick={() => {
                                      if (!saleNo) { message.error('판매번호가 없습니다.'); return; }
                                      setDirectPreDepositMode(true);
                                      setGroupOpen(true);
                                    }}
                                  >행사 전도금 내역</AntButton>
                                )}
                              </div>
                              <div className="pl-3 mt-3">
                                {preDepLoading && (
                                  <div className="text-xs text-gray-500">행사 전도금 내역 불러오는 중...</div>
                                )}
                                {!preDepLoading && preDepError && (
                                  <div className="text-xs text-red-600">{preDepError}</div>
                                )}
                                {!preDepLoading && !preDepError && (
                                  <div className="overflow-x-auto">
                                    {(preDepRows || []).length === 0 ? (
                                      <div className="text-xs text-gray-500">전도금 내역이 없습니다.</div>
                                    ) : (
                                      <table className="min-w-full text-xs">
                                        <thead>
                                          <tr className="text-gray-600">
                                            <th className="px-2 py-1 text-left whitespace-nowrap">지급일자</th>
                                            <th className="px-2 py-1 text-left whitespace-nowrap">금액</th>
                                            <th className="px-2 py-1 text-left whitespace-nowrap">비고</th>
                                            <th className="px-2 py-1 text-left whitespace-nowrap">결재상태</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(preDepRows || []).map((row, idx) => (
                                            <tr key={idx} className="border-t border-gray-100">
                                              <td className="px-2 py-1 text-gray-800">
                                                {row?.매핑출납일 || row?.출납일 || row?.지급일자 || row?.등록일자 || ''}
                                              </td>
                                              <td className="px-2 py-1 text-left tabular-nums">
                                                {formatNumberWithCommas(row?.지급액 || row?.전도금액 || row?.지급금액 || 0)}
                                              </td>
                                              <td className="px-2 py-1 text-gray-800">
                                                {row?.비고 || ''}
                                              </td>
                                              <td className="px-2 py-1 text-left text-gray-800">
                                                {row?.결재상태 || row?.상태 || ''}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* Extra services details */}
                          {(extraLoading && (
                            <div className="text-xs text-gray-500 mt-3">추가 서비스 정보를 불러오는 중...</div>
                          ))}
                          {(!extraLoading && extraError) && (
                            <div className="text-xs text-red-600 mt-3">{extraError}</div>
                          )}
                          {(!extraLoading && !extraError && (extraServices || []).length > 0) && (
                            <div className="mt-4 p-3 border border-gray-200 rounded bg-white">
                              <div className="text-[12px] text-gray-600 mb-2">※추가 서비스(옵션) 상세 정보</div>
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-xs">
                                  <thead>
                                    <tr className="text-gray-600">
                                      <th className="px-2 py-1 text-left">서비스명</th>
                                      <th className="px-2 py-1 text-right whitespace-nowrap">성인수량</th>
                                      <th className="px-2 py-1 text-right whitespace-nowrap">소아수량</th>
                                      <th className="px-2 py-1 text-right whitespace-nowrap">유아수량</th>
                                      <th className="px-2 py-1 text-right whitespace-nowrap">판매금액</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(extraServices || []).map((row, idx) => (
                                      <tr key={idx} className="border-t border-gray-100">
                                        <td className="px-2 py-1 text-gray-800">{row?.서비스명 || ''}</td>
                                        <td className="px-2 py-1 text-right tabular-nums">{formatNumberWithCommas(row?.성인수량 || 0)}</td>
                                        <td className="px-2 py-1 text-right tabular-nums">{formatNumberWithCommas(row?.소아수량 || 0)}</td>
                                        <td className="px-2 py-1 text-right tabular-nums">{formatNumberWithCommas(row?.유아수량 || 0)}</td>
                                        <td className="px-2 py-1 text-right tabular-nums">{cur}{formatNumberWithCommas(row?.판매금액 || 0)}</td>
                                      </tr>
                                    ))}
                                    <tr className="border-t">
                                      <td className="px-2 py-1 font-semibold text-right" colSpan={4}>합계</td>
                                      <td className="px-2 py-1 text-right tabular-nums font-semibold">{cur}{formatNumberWithCommas(추가서비스합계)}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div className="px-8 py-2">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1">
                    <div className="text-base font-semibold text-gray-800 flex items-center gap-2">
                      <span className="inline-block w-1 h-4 bg-blue-600 rounded-sm" />
                      업무메모
                    </div>
                    <AntButton
                      size="small"
                      type="text"
                      icon={<PlusOutlined />}
                      disabled={!saleNo || logsLoading}
                      onClick={() => {
                        if (!saleNo) { message.error('판매번호가 없어 업무메모를 추가할 수 없습니다.'); return; }
                        setMemoAddText('');
                        setMemoAddOpen(true);
                      }}
                      title={!saleNo ? '판매번호가 없어 추가할 수 없습니다.' : '메모 추가'}
                    />
                  </div>
                  {logsLoading && <div className="text-xs text-gray-500">불러오는 중...</div>}
                  {!logsLoading && logsError && <div className="text-xs text-red-600">{logsError}</div>}
                </div>
                {!logsLoading && !logsError && (
                  (() => {
                    const memos = (reservationLogs || [])
                      .filter((r) => String(r?.태그 || '').trim() === '업무메모')
                      .map((r, idx) => ({ key: idx, row: r, 작성일: r?.작성일, 내용: r?.내용 }))
                      .sort((a, b) => String(b?.작성일 || '').localeCompare(String(a?.작성일 || '')));

                    if (memos.length === 0) {
                      return <div className="text-sm text-gray-500">등록된 업무메모가 없습니다.</div>;
                    }

                    return (
                      <div className="max-h-40 overflow-auto space-y-2">
                        {memos.map((m) => {
                          const authorNo = getLogAuthorUserNo(m?.row);
                          const canEdit = authorNo && currentUserNo && authorNo === currentUserNo;
                          const refNo = getLogReferenceNo(m?.row);
                          const editDisabled = !refNo;
                          return (
                            <div key={m.key} className="flex items-start gap-2">
                              <div className="w-[150px] shrink-0 flex items-center justify-center gap-1">
                                <div className="w-7 shrink-0 flex items-center justify-center h-5">
                                  {canEdit ? (
                                    <AntButton
                                      size="small"
                                      type="text"
                                      icon={<EditOutlined />}
                                      className="!w-5 !h-5 !min-w-0 !p-0 flex items-center justify-center"
                                      disabled={editDisabled}
                                      onClick={() => {
                                        if (!refNo) { message.error('참조번호가 없어 수정할 수 없습니다.'); return; }
                                        setMemoEditRow(m.row);
                                        setMemoEditText(String(m?.row?.내용 ?? ''));
                                        setMemoEditOpen(true);
                                      }}
                                      title={editDisabled ? '참조번호가 없어 수정할 수 없습니다.' : '수정'}
                                    />
                                  ) : (
                                    <span />
                                  )}
                                </div>
                                <div className="text-xs text-gray-500 whitespace-nowrap">
                                  {dayjs(String(m?.작성일 || '')).format('MM-DD HH:mm')}
                                </div>
                              </div>
                              <div className="flex-1 text-xs text-gray-800 whitespace-pre-wrap break-words">
                                {m.내용 || '-'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          </div>
        )}

        {/* Extra services detail modal */}
        <AntModal
          open={extraModalOpen}
          onCancel={() => setExtraModalOpen(false)}
          footer={null}
          title="추가 서비스(옵션) 상세 정보"
          width={720}
          centered
        >
          {(extraLoading && (
            <div className="text-xs text-gray-500 mt-1">추가 서비스 정보를 불러오는 중...</div>
          ))}
          {(!extraLoading && extraError) && (
            <div className="text-xs text-red-600 mt-1">{extraError}</div>
          )}
          {(!extraLoading && !extraError && (extraServices || []).length === 0) && (
            <div className="text-xs text-gray-500 mt-1">추가 서비스(옵션) 정보가 없습니다.</div>
          )}
          {(!extraLoading && !extraError && (extraServices || []).length > 0) && (
            <div className="mt-2">
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-gray-600">
                      <th className="px-2 py-1 text-left">서비스명</th>
                      <th className="px-2 py-1 text-right whitespace-nowrap">성인수량</th>
                      <th className="px-2 py-1 text-right whitespace-nowrap">소아수량</th>
                      <th className="px-2 py-1 text-right whitespace-nowrap">유아수량</th>
                      <th className="px-2 py-1 text-right whitespace-nowrap">판매금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(extraServices || []).map((row, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        <td className="px-2 py-1 text-gray-800">{row?.서비스명 || ''}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{formatNumberWithCommas(row?.성인수량 || 0)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{formatNumberWithCommas(row?.소아수량 || 0)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{formatNumberWithCommas(row?.유아수량 || 0)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{formatNumberWithCommas(row?.판매금액 || 0)}</td>
                      </tr>
                    ))}
                    <tr className="border-t">
                      <td className="px-2 py-1 font-semibold text-right" colSpan={4}>합계</td>
                      <td className="px-2 py-1 text-right tabular-nums font-semibold">{formatNumberWithCommas((Array.isArray(extraServices) ? extraServices : []).reduce((sum, r) => sum + (Number(r?.판매금액) || 0), 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </AntModal>

        <AntModal
          open={addTravelerAgeModalOpen}
          title="추가인원 연령 선택"
          centered
          maskClosable={false}
          onCancel={() => setAddTravelerAgeModalOpen(false)}
          onOk={confirmAddTravelerWithAge}
          okText="추가"
          cancelText="취소"
        >
          <div className="space-y-3">
            
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-gray-600 w-20">예약연령</span>
              <AntSelect
                value={addTravelerAge}
                options={ageOptionsForAdd}
                onChange={(v) => setAddTravelerAge(v)}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </AntModal>

        <AntModal
          open={passportOcrModalOpen}
          onCancel={() => {
            if (passportOcrSaving) return;
            setPassportOcrModalOpen(false);
            setPassportOcrTargetKey(null);
            setPassportOcrRaw(null);
            setPassportOcrPatch({});
            setPassportOcrApply({});
          }}
          title="여권 판독 결과 적용"
          width={1100}
          centered
          okText={null}
          cancelText={null}
          footer={
            <div className="flex justify-end gap-2">
              <AntButton
                onClick={() => {
                  if (passportOcrSaving) return;
                  setPassportOcrModalOpen(false);
                  setPassportOcrTargetKey(null);
                  setPassportOcrRaw(null);
                  setPassportOcrPatch({});
                  setPassportOcrApply({});
                }}
              >
                닫기
              </AntButton>
              <AntButton
                type="primary"
                disabled={passportOcrSaving}
                onClick={() => {
                  const targetKey = passportOcrTargetKey;
                  const patch = passportOcrPatch || {};
                  const apply = passportOcrApply || {};
                  const selected = {};
                  Object.keys(patch).forEach((k) => {
                    if (apply[k]) selected[k] = patch[k];
                  });
                  if (Object.keys(selected).length === 0) {
                    message.info('적용할 항목이 없습니다.');
                    return;
                  }
                  setTravelers((prev) => (Array.isArray(prev) ? prev.map((t) => (t.key === targetKey ? { ...t, ...selected } : t)) : prev));
                  message.success('판독 결과가 적용되었습니다. 저장 버튼을 눌러 반영해 주세요.');
                  setPassportOcrModalOpen(false);
                  setPassportOcrTargetKey(null);
                  setPassportOcrRaw(null);
                  setPassportOcrPatch({});
                  setPassportOcrApply({});
                }}
              >
                적용
              </AntButton>
              <AntButton
                type="primary"
                loading={passportOcrSaving}
                onClick={async () => {
                  const targetKey = passportOcrTargetKey;
                  const patch = passportOcrPatch || {};
                  const apply = passportOcrApply || {};
                  const selected = {};
                  Object.keys(patch).forEach((k) => {
                    if (apply[k]) selected[k] = patch[k];
                  });
                  if (Object.keys(selected).length === 0) {
                    message.info('적용할 항목이 없습니다.');
                    return;
                  }
                  const nextTravelers = (Array.isArray(travelers) ? travelers : []).map((t) => (t.key === targetKey ? { ...t, ...selected } : t));
                  setTravelers(nextTravelers);
                  try {
                    setPassportOcrSaving(true);
                    await handleSave(nextTravelers);
                    setPassportOcrModalOpen(false);
                    setPassportOcrTargetKey(null);
                    setPassportOcrRaw(null);
                    setPassportOcrPatch({});
                    setPassportOcrApply({});
                  } catch (e) {
                    message.error(e?.message || '저장에 실패했습니다.');
                  } finally {
                    setPassportOcrSaving(false);
                  }
                }}
              >
                적용 후 저장
              </AntButton>
            </div>
          }
        >
          <div className="space-y-3">
            <div className="text-xs text-gray-500">
              업로드한 여권사본을 분석한 결과입니다. 반영할 항목을 선택하세요.
            </div>
            <div className="grid grid-cols-1 gap-2">
              {(function () {
                const target = (Array.isArray(travelers) ? travelers : []).find((t) => t.key === passportOcrTargetKey) || {};
                const patch = passportOcrPatch || {};
                const rawOcr = passportOcrRaw?.ocr || {};
                const passportUrl = buildPassportObjectUrl(target?.여권사본경로 || passportOcrRaw?.url || '');
                const isPdf = passportUrl ? /\.pdf(\?|#|$)/i.test(passportUrl) : false;
                const includeKeys = new Set([
                  ...Object.keys(passportOcrApply || {}),
                  ...Object.keys(patch || {}),
                ]);
                const rows = [
                  { k: '한글이름', label: '한글이름', cur: target?.한글이름 || '' },
                  { k: '영문성', label: '영문성', cur: target?.영문성 || '' },
                  { k: '영문이름', label: '영문이름', cur: target?.영문이름 || '' },
                  { k: '여권번호', label: '여권번호', cur: target?.여권번호 || '' },
                  { k: '생년월일', label: '생년월일', cur: target?.생년월일 || '' },
                  { k: '여권만료일', label: '여권만료일', cur: target?.여권만료일 || '' },
                  { k: '성별', label: '성별', cur: target?.성별 || '' },
                ].filter((r) => includeKeys.has(r.k));

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="border border-gray-200 rounded p-3 bg-white">
                      <div className="text-[11px] text-gray-600 mb-2">업로드한 여권사본</div>
                      {!passportUrl ? (
                        <div className="text-sm text-gray-500">여권사본을 불러올 수 없습니다.</div>
                      ) : isPdf ? (
                        <a
                          href={passportUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          여권사본(PDF) 열기
                        </a>
                      ) : (
                        <div className="w-full overflow-hidden rounded border border-gray-100 bg-gray-50" style={{ maxHeight: 360 }}>
                          <AntImage
                            src={passportUrl}
                            alt="passport"
                            style={{ width: '100%', maxHeight: 360, objectFit: 'contain' }}
                          />
                        </div>
                      )}
                    </div>

                    <div className="border border-gray-200 rounded">
                      <div className="grid grid-cols-12 gap-0 bg-gray-50 border-b border-gray-200 text-[11px] text-gray-600">
                        <div className="col-span-2 px-2 py-2">적용</div>
                        <div className="col-span-3 px-2 py-2">항목</div>
                        <div className="col-span-3 px-2 py-2">OCR</div>
                        <div className="col-span-4 px-2 py-2">현재</div>
                      </div>
                      {rows.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-gray-500">표시할 OCR 항목이 없습니다.</div>
                      ) : (
                        rows.map((r) => (
                          <div key={r.k} className="grid grid-cols-12 gap-0 border-b last:border-b-0 border-gray-100 text-[12px]">
                            <div className="col-span-2 px-2 py-2">
                              <AntCheckbox
                                checked={!!passportOcrApply?.[r.k]}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setPassportOcrApply((prev) => ({ ...(prev || {}), [r.k]: checked }));
                                }}
                              />
                            </div>
                            <div className="col-span-3 px-2 py-2 text-gray-700">{r.label}</div>
                            <div className="col-span-3 px-2 py-2">
                              {r.k === '성별' ? (
                                <AntSelect
                                  size="small"
                                  value={passportOcrPatch?.성별 ?? undefined}
                                  placeholder="선택"
                                  options={[{ value: '남성', label: '남성' }, { value: '여성', label: '여성' }]}
                                  onChange={(val) => setPassportOcrPatch((prev) => ({ ...(prev || {}), 성별: val }))}
                                  style={{ width: '100%' }}
                                />
                              ) : (r.k === '생년월일' || r.k === '여권만료일') ? (
                                <DateFieldEditor
                                  value={passportOcrPatch?.[r.k] || ''}
                                  disabled={false}
                                  onChange={(val) => setPassportOcrPatch((prev) => ({ ...(prev || {}), [r.k]: val }))}
                                />
                              ) : (
                                <AntInput
                                  size="small"
                                  value={passportOcrPatch?.[r.k] ?? ''}
                                  onChange={(e) => setPassportOcrPatch((prev) => ({ ...(prev || {}), [r.k]: e.target.value }))}
                                />
                              )}
                            </div>
                            <div className="col-span-4 px-2 py-2 text-gray-600 break-all">{r.cur || '-'}</div>
                          </div>
                        ))
                      )}
                      {String(rawOcr?.발급일 || '').trim() ? (
                        <div className="px-3 py-2 text-[11px] text-gray-500 border-t border-gray-200">
                          발급일: {String(rawOcr?.발급일 || '').trim()}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </AntModal>

        {/* Traveler cancel fee modal */}
        <AntModal
          open={travelerCancelModalOpen}
          onCancel={() => {
            setTravelerCancelModalOpen(false);
            setTravelerCancelTargetKey(null);
          }}
          onOk={async () => {
            const amount = Number(travelerCancelAmount) || 0;
            if (amount < 0) {
              message.error('취소료는 0 이상이어야 합니다.');
              return;
            }
            if (!travelerCancelTargetKey) {
              setTravelerCancelModalOpen(false);
              return;
            }
            const nextTravelers = (travelers || []).map((r) => (
              r.key === travelerCancelTargetKey
                ? {
                  ...r,
                  여행객종류: '취소',
                  상품금액원화: amount,
                  택스및유류원화: 0,
                }
                : r
            ));
            setTravelers(nextTravelers);
            setTravelerCancelModalOpen(false);
            await handleSave(nextTravelers);
            message.success('취소료가 적용되었습니다.');
          }}
          title="여행객 취소료 입력"
          maskClosable={false}
          centered
        >
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              이 여행객의 취소료를 입력하세요. 입력된 금액은 해당 여행객의 상품금액(취소료)으로 저장되며, 텍스 및 유류는 0으로 처리됩니다.
            </p>
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-gray-600 w-16">취소료</span>
              <AntInputNumber
                min={0}
                value={travelerCancelAmount}
                onChange={(v) => setTravelerCancelAmount(Number(v) || 0)}
                style={{ width: '100%' }}
                formatter={(val) => formatNumberWithCommas(typeof val === 'number' ? val : Number(val || 0))}
                parser={(val) => (val ? val.toString().replace(/,/g, '') : '')}
              />
            </div>
            <p className="text-[11px] text-gray-500">
              ※ 실제 DB 반영을 위해서는 상단의 "저장" 버튼을 눌러야 합니다.
            </p>
          </div>
        </AntModal>

        {/* Traveler restore (un-cancel) modal */}
        <AntModal
          open={travelerRestoreModalOpen}
          onCancel={() => {
            setTravelerRestoreModalOpen(false);
            setTravelerRestoreTargetKey(null);
          }}
          onOk={async () => {
            const price = Number(travelerRestorePrice) || 0;
            const taxFuel = Number(travelerRestoreTaxFuel) || 0;
            if (price < 0 || taxFuel < 0) {
              message.error('상품가와 택스 및 유류는 0 이상이어야 합니다.');
              message.error('상품가와 텍스 및 유류는 0 이상이어야 합니다.');
              return;
            }
            if (!travelerRestoreTargetKey) {
              setTravelerRestoreModalOpen(false);
              return;
            }
            const nextTravelers = (travelers || []).map((r) => (
              r.key === travelerRestoreTargetKey
                ? {
                  ...r,
                  여행객종류: '여행객',
                  상품금액원화: price,
                  택스및유류원화: taxFuel,
                }
                : r
            ));
            setTravelers(nextTravelers);
            setTravelerRestoreModalOpen(false);
            await handleSave(nextTravelers);
            message.success('취소가 해제되었습니다.');
          }}
          title="여행객 취소 해제 금액 입력"
          maskClosable={false}
          centered
        >
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              취소를 해제하면서 이 여행객의 상품가와 텍스 및 유류 금액을 입력하세요. 입력된 금액은 해당 여행객의 상품금액 및 텍스/유류 금액으로 저장됩니다.
            </p>
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-gray-600 w-20">상품가</span>
              <AntInputNumber
                min={0}
                value={travelerRestorePrice}
                onChange={(v) => setTravelerRestorePrice(Number(v) || 0)}
                style={{ width: '100%' }}
                formatter={(val) => formatNumberWithCommas(typeof val === 'number' ? val : Number(val || 0))}
                parser={(val) => (val ? val.toString().replace(/,/g, '') : '')}
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-gray-600 w-20">텍스 및 유류</span>
              <AntInputNumber
                min={0}
                value={travelerRestoreTaxFuel}
                onChange={(v) => setTravelerRestoreTaxFuel(Number(v) || 0)}
                style={{ width: '100%' }}
                formatter={(val) => formatNumberWithCommas(typeof val === 'number' ? val : Number(val || 0))}
                parser={(val) => (val ? val.toString().replace(/,/g, '') : '')}
              />
            </div>
            <p className="text-[11px] text-gray-500">
              ※ 실제 DB 반영을 위해서는 상단의 "저장" 버튼을 눌러야 합니다.
            </p>
          </div>
        </AntModal>

        <AntModal
          open={agencyCommissionModalOpen}
          onCancel={() => {
            setAgencyCommissionModalOpen(false);
            const p = priceInfo || {};
            const current = Number(p?.대리점커미션원화 ?? p?.대리점커미션 ?? p?.커미션원화 ?? 0) || 0;
            setAgencyCommissionDraft(current);
          }}
          footer={null}
          centered
          maskClosable={false}
          width={420}
          title="대리점 커미션 수정"
        >
          <div className="space-y-3">
            <div className="text-[11px] text-gray-500">금액</div>
            <AntInputNumber
              min={0}
              value={agencyCommissionDraft}
              onChange={(v) => setAgencyCommissionDraft(Number(v) || 0)}
              style={{ width: '100%' }}
              formatter={(val) => formatNumberWithCommas(typeof val === 'number' ? val : Number(val || 0))}
              parser={(val) => (val ? val.toString().replace(/,/g, '') : '')}
              disabled={agencyCommissionSaving}
            />
            <div className="flex justify-end gap-2 pt-2">
              <AntButton size="small" onClick={() => setAgencyCommissionModalOpen(false)} disabled={agencyCommissionSaving}>취소</AntButton>
              <AntButton size="small" type="primary" loading={agencyCommissionSaving} onClick={handleAgencyCommissionSave}>저장</AntButton>
            </div>
          </div>
        </AntModal>

        {/* Payment Edit Modal */}
        <AntModal
          open={paymentModalOpen}
          onCancel={closePaymentModal}
          footer={null}
          centered
          maskClosable={false}
          width={1040}
          bodyStyle={{ background: '#f8fafc' }}
          title={(
            <div className="flex items-center gap-2">
              <span>입금내역 편집</span>
              <AntTooltip title="입금내역 편집 화면 설명">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPaymentHelpOpen(true);
                  }}
                  className="inline-flex items-center justify-center rounded-full border border-transparent text-xs text-sky-700 hover:text-sky-900 hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-1"
                  style={{ width: 22, height: 22 }}
                >
                  <InfoCircleOutlined style={{ fontSize: 14 }} />
                </button>
              </AntTooltip>
            </div>
          )}
        >
          {paymentLoading && <div className="text-sm text-gray-500">불러오는 중...</div>}
          {!paymentLoading && paymentError && <div className="text-sm text-red-600">{paymentError}</div>}
          {!paymentLoading && !paymentError && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg border bg-white shadow-sm">
                  <div className="text-[11px] text-gray-500">현재 미수금</div>
                  <div className="mt-1 text-lg font-semibold text-gray-900 tabular-nums">
                    {depositSummary?.미수금 === 0 ? (
                      <span className="text-green-600">완납</span>
                    ) : (
                      <span>{formatNumberWithCommas(depositSummary?.미수금 ?? 0)}</span>
                    )}
                  </div>
                </div>
                <div className="p-4 rounded-lg border bg-white shadow-sm">
                  <div className="text-[11px] text-gray-500">현재 입금액</div>
                  <div className="mt-1 text-lg font-semibold text-gray-900 tabular-nums">{formatNumberWithCommas(depositSummary?.입금액 ?? 0)}</div>
                </div>
              </div>

              {/* Deposit list */}
              <div className="rounded-lg border bg-white">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-800">입금 내역</h4>
                </div>
                <div className="p-3">
                  {(depositList || []).length === 0 ? (
                    <div className="text-xs text-gray-500">입금 내역이 없습니다.</div>
                  ) : (
                    <Table
                      size="small"
                      bordered
                      pagination={false}
                      sticky
                      scroll={{ x: 820, y: 220 }}
                      dataSource={(depositList || []).map((r, idx) => ({ key: idx, ...r }))}
                      columns={[
                        { title: '입금일', dataIndex: '수납일', key: '수납일', width: 120, align: 'center' },
                        { title: '송금(카드소유)자', dataIndex: '송금카드소유자', key: '송금카드소유자', width: 160, align: 'center' },
                        { title: '결제구분', dataIndex: '결제구분', key: '결제구분', width: 120, align: 'center' },
                        { title: '입금액', dataIndex: '입금액', key: '입금액', width: 140, align: 'center', render: (v) => formatNumberWithCommas(v) },
                        {
                          title: '작업', key: 'actions', width: 90, align: 'center',
                          render: (_v, record) => (
                            <Popconfirm
                              title="이 입금 내역을 삭제하시겠습니까?"
                              okText="삭제"
                              cancelText="취소"
                              onConfirm={async () => {
                                const paymentNo = record?.결제번호;
                                if (!paymentNo) { message.error('결제번호가 없어 삭제할 수 없습니다.'); return; }
                                try {
                                  setDeletingPaymentId(paymentNo);
                                  const hide = message.loading('삭제 중...', 0);
                                  await deleteOrderPayment({ paymentNo });
                                  hide();
                                  message.success('삭제되었습니다.');
                                  await refreshPaymentModalData();
                                } catch (e) {
                                  message.error(e?.message || '삭제에 실패했습니다.');
                                } finally {
                                  setDeletingPaymentId(null);
                                }
                              }}
                            >
                              <AntButton type="link" danger size="small" loading={deletingPaymentId === record?.결제번호}>삭제</AntButton>
                            </Popconfirm>
                          )
                        }
                      ]}
                    />
                  )}
                </div>
              </div>

              {/* Receipt list + apply */}
              <div className="rounded-lg border bg-white">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
                    <h4 className="text-sm font-semibold text-gray-800" style={{ whiteSpace: 'nowrap' }}>통장 입금 내역</h4>
                    <div className="flex items-center gap-1" style={{ flexWrap: 'wrap' }}>
                      <AntButton size="small" type={receiptRangePreset === 'today' ? 'primary' : 'default'} onClick={() => applyReceiptRangePreset('today')}>오늘</AntButton>
                      <AntButton size="small" type={receiptRangePreset === '1w' ? 'primary' : 'default'} onClick={() => applyReceiptRangePreset('1w')}>일주일</AntButton>
                      <AntButton size="small" type={receiptRangePreset === '1m' ? 'primary' : 'default'} onClick={() => applyReceiptRangePreset('1m')}>1개월</AntButton>
                      <AntButton size="small" type={receiptRangePreset === '3m' ? 'primary' : 'default'} onClick={() => applyReceiptRangePreset('3m')}>3개월</AntButton>
                      <AntButton size="small" type={receiptRangePreset === 'custom' ? 'primary' : 'default'} onClick={() => applyReceiptRangePreset('custom')}>기간 지정</AntButton>
                    </div>
                    {receiptRangePreset === 'custom' && (
                      <div style={{ minWidth: 240 }}>
                        <AntDatePicker.RangePicker
                          size="small"
                          value={(receiptStartDate && receiptEndDate && isValidYmd(receiptStartDate) && isValidYmd(receiptEndDate)) ? [dayjs(receiptStartDate), dayjs(receiptEndDate)] : null}
                          onChange={onChangeReceiptCustomRange}
                          format="YYYY-MM-DD"
                          allowClear
                        />
                      </div>
                    )}
                  </div>
                  <AntButton
                    size="small"
                    type="primary"
                    onClick={() => setShowAddReceiptForm(!showAddReceiptForm)}
                  >
                    {showAddReceiptForm ? '취소' : '내역 추가'}
                  </AntButton>
                </div>
                <div className="p-3">
                  {/* Add Receipt Form */}
                  {showAddReceiptForm && (
                    <div className="mb-4 p-4 rounded-lg bg-gray-100">
                      <div className="text-sm font-semibold text-gray-800 mb-3">새 입금 등록</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <div className="text-[11px] text-gray-600 mb-1">입금일 *</div>
                          <DateFieldEditor
                            value={newReceiptDate}
                            onChange={setNewReceiptDate}
                            placeholder="YYYY-MM-DD"
                            width="100%"
                          />
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-600 mb-1">입금액 *</div>
                          <AntInputNumber
                            size="small"
                            min={0}
                            value={newReceiptAmount}
                            onChange={(v) => setNewReceiptAmount(Number(v) || null)}
                            style={{ width: '100%' }}
                            placeholder="수납액 입력"
                            formatter={(val) => formatNumberWithCommas(typeof val === 'number' ? val : Number(val || 0))}
                            parser={(val) => (val ? val.toString().replace(/,/g, '') : '')}
                          />
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-600 mb-1">입금자 *</div>
                          <AntInput
                            size="small"
                            value={newReceiptDepositor}
                            onChange={(e) => setNewReceiptDepositor(e.target.value)}
                            placeholder="입금자명 입력"
                          />
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-600 mb-1">입금 계좌 *</div>
                          <AntSelect
                            size="small"
                            value={newReceiptAccountId || undefined}
                            onChange={(val) => setNewReceiptAccountId(val)}
                            style={{ width: '100%' }}
                            placeholder={bankAccountsLoading ? '계좌 불러오는 중...' : '입금 계좌 선택'}
                            loading={bankAccountsLoading}
                            options={(bankAccounts || []).map((acc) => ({
                              value: String(acc.계좌일련번호 ?? ''),
                              label: `${acc.은행명 ?? ''} / ${acc.계좌번호 ?? ''}`,
                            }))}
                          />
                          {bankAccountsError && (
                            <div className="mt-1 text-[11px] text-red-600">{bankAccountsError}</div>
                          )}
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-600 mb-1">비고사항</div>
                          <AntInput
                            size="small"
                            value={newReceiptNote}
                            onChange={(e) => setNewReceiptNote(e.target.value)}
                            placeholder="비고 입력 (선택)"
                          />
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <AntCheckbox
                          checked={applyImmediately}
                          onChange={(e) => setApplyImmediately(e.target.checked)}
                        >
                          <span className="text-xs text-gray-700">등록 후 바로 적용</span>
                        </AntCheckbox>
                        <div className="flex gap-2">
                          <AntButton
                            size="small"
                            onClick={() => {
                              setShowAddReceiptForm(false);
                              resetNewReceiptForm();
                            }}
                          >
                            취소
                          </AntButton>
                          <AntButton
                            type="primary"
                            size="small"
                            loading={addingReceipt}
                            onClick={handleAddReceipt}
                          >
                            저장
                          </AntButton>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  {(receiptList || []).length === 0 ? (
                    <div className="text-xs text-gray-500">표시할 입금 내역이 없습니다.</div>
                  ) : (
                    <Table
                      size="small"
                      bordered
                      pagination={false}
                      sticky
                      scroll={{ x: 760, y: 260 }}
                      dataSource={receiptTableData}
                      rowSelection={{
                        type: 'radio',
                        selectedRowKeys: selectedReceiptTxNo ? [String(selectedReceiptTxNo)] : [],
                        onChange: async (keys, rows) => {
                          const row = rows?.[0];
                          const tx = row?.거래번호;
                          setSelectedReceiptTxNo(tx || null);
                          setSelectedReceiptRow(row || null);
                          setReceiptDetail(null);
                          setApplyAmount(null);
                          if (tx) {
                            try {
                              const detail = await getReceiptDetailByTransactionNo({ saleNo, transactionNo: tx });
                              setReceiptDetail(detail);
                              const maxByBalance = Number(detail?.잔액) || 0;
                              const maxByDue = Number(depositSummary?.미수금) || 0;
                              const maxAllowed = Math.max(0, Math.min(maxByBalance, maxByDue));
                              setApplyAmount(maxAllowed || null);
                            } catch (e) {
                              message.error(e?.message || '수납 상세를 불러오지 못했습니다.');
                            }
                          }
                        },
                        getCheckboxProps: (record) => ({ disabled: false })
                      }}
                      columns={[
                        {
                          title: '입금일',
                          dataIndex: '수납일',
                          key: '수납일',
                          width: 120,
                          align: 'center',
                          sorter: (a, b) => compareYmdLike(a?.수납일, b?.수납일),
                          defaultSortOrder: 'descend',
                          ...buildTextColumnFilter('수납일'),
                        },
                        {
                          title: '송금(카드소유)자',
                          dataIndex: '송급카드소유자',
                          key: '송급카드소유자',
                          width: 160,
                          align: 'center',
                          sorter: (a, b) => compareKoreanLike(a?.송급카드소유자, b?.송급카드소유자),
                          ...buildTextColumnFilter('송급카드소유자'),
                        },
                        {
                          title: '종류',
                          dataIndex: '종류',
                          key: '종류',
                          width: 100,
                          align: 'center',
                          sorter: (a, b) => compareKoreanLike(a?.종류, b?.종류),
                          ...buildTextColumnFilter('종류'),
                        },
                        {
                          title: '입금액',
                          dataIndex: '수납액',
                          key: '수납액',
                          width: 120,
                          align: 'center',
                          render: (v) => formatNumberWithCommas(v),
                          sorter: (a, b) => compareNumberLike(a?.수납액, b?.수납액),
                          ...buildNumberRangeColumnFilter('수납액'),
                        },
                        {
                          title: '사용액',
                          dataIndex: '총입금액',
                          key: '총입금액',
                          width: 120,
                          align: 'center',
                          render: (v) => formatNumberWithCommas(v),
                          sorter: (a, b) => compareNumberLike(a?.총입금액, b?.총입금액),
                          ...buildNumberRangeColumnFilter('총입금액'),
                        },
                        {
                          title: '잔액',
                          dataIndex: '잔액',
                          key: '잔액',
                          width: 120,
                          align: 'center',
                          render: (v) => formatNumberWithCommas(v),
                          sorter: (a, b) => compareNumberLike(a?.잔액, b?.잔액),
                          ...buildNumberRangeColumnFilter('잔액'),
                        },
                      ]}
                    />
                  )}

                  {/* Apply form */}
                  <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <div className="mb-2 text-sm font-semibold text-gray-700">선택 입금 내역 적용</div>
                    {!selectedReceiptTxNo && (
                      <div className="text-xs text-gray-500">입금 내역에서 항목을 선택하세요.</div>
                    )}
                    {selectedReceiptTxNo && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                        <div>
                          <div className="text-[11px] text-gray-500 mb-1">최대 적용 가능 금액</div>
                          <div className="text-base font-medium tabular-nums">
                            {(() => {
                              const maxByBalance = Number(receiptDetail?.잔액) || 0;
                              const maxByDue = Number(depositSummary?.미수금) || 0;
                              const maxAllowed = Math.max(0, Math.min(maxByBalance, maxByDue));
                              return formatNumberWithCommas(maxAllowed);
                            })()}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-500 mb-1 flex items-center justify-between">
                            <span>적용할 금액</span>
                            <AntButton
                              size="small"
                              type="text"
                              onClick={() => {
                                const maxByBalance = Number(receiptDetail?.잔액) || 0;
                                const maxByDue = Number(depositSummary?.미수금) || 0;
                                const maxAllowed = Math.max(0, Math.min(maxByBalance, maxByDue));
                                setApplyAmount(maxAllowed);
                              }}
                            >
                              최대
                            </AntButton>
                          </div>
                          {(() => {
                            const maxByBalance = Number(receiptDetail?.잔액) || 0;
                            const maxByDue = Number(depositSummary?.미수금) || 0;
                            const maxAllowed = Math.max(0, Math.min(maxByBalance, maxByDue));
                            const amt = Number(applyAmount) || 0;
                            const invalid = amt <= 0 || amt > maxAllowed;
                            return (
                              <div>
                                <AntInputNumber
                                  size="small"
                                  min={0}
                                  value={applyAmount}
                                  onChange={(v) => setApplyAmount(Number(v) || 0)}
                                  style={{ width: '100%' }}
                                  formatter={(val) => formatNumberWithCommas(typeof val === 'number' ? val : Number(val || 0))}
                                  parser={(val) => (val ? val.toString().replace(/,/g, '') : '')}
                                  onPressEnter={(e) => {
                                    e.preventDefault();
                                    if (!invalid) {
                                      const btn = document.getElementById('apply-receipt-submit');
                                      btn && btn.click();
                                    }
                                  }}
                                />
                                {invalid && (
                                  <div className="text-[11px] text-red-600 mt-1">0보다 크고 최대 가능 금액 이하여야 합니다.</div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <div className="sm:col-span-2">
                          <div className="text-[11px] text-gray-500 mb-1">비고</div>
                          <AntInput
                            size="small"
                            value={applyNote}
                            onChange={(e) => setApplyNote(e.target.value)}
                            onPressEnter={(e) => {
                              e.preventDefault();
                              const maxByBalance = Number(receiptDetail?.잔액) || 0;
                              const maxByDue = Number(depositSummary?.미수금) || 0;
                              const maxAllowed = Math.max(0, Math.min(maxByBalance, maxByDue));
                              const amt = Number(applyAmount) || 0;
                              if (amt > 0 && amt <= maxAllowed) {
                                const btn = document.getElementById('apply-receipt-submit');
                                btn && btn.click();
                              }
                            }}
                          />
                        </div>
                        <div className="sm:col-span-2 flex justify-between items-center">
                          <Popconfirm
                            title="선택한 수납 내역을 삭제하시겠습니까?"
                            description="삭제된 수납은 복구할 수 없습니다."
                            okText="삭제"
                            cancelText="취소"
                            okButtonProps={{ danger: true }}
                            onConfirm={async () => {
                              if (!selectedReceiptTxNo || !selectedReceiptRow) {
                                message.error('선택된 수납이 없습니다.');
                                return;
                              }
                              // Extract transaction serial number from the selected row
                              // The 거래일련번호 field contains the numeric transaction serial number
                              const txSerialNo = selectedReceiptRow?.거래일련번호;
                              if (!txSerialNo) {
                                message.error('거래일련번호를 찾을 수 없습니다.');
                                return;
                              }
                              try {
                                setDeletingReceiptTxNo(selectedReceiptTxNo);
                                const hide = message.loading('수납 삭제 중...', 0);
await deleteReceiptRecord({ transactionNo: txSerialNo });
                                hide();
                                message.success('수납이 삭제되었습니다.');
                                setSelectedReceiptTxNo(null);
                                setSelectedReceiptRow(null);
                                setReceiptDetail(null);
                                setApplyAmount(null);
                                setApplyNote('');
                                await refreshPaymentModalData();
                              } catch (e) {
                                console.error('수납 삭제 실패:', e);
                                message.error(e?.message || '수납 삭제에 실패했습니다.');
                              } finally {
                                setDeletingReceiptTxNo(null);
                              }
                            }}
                          >

                          </Popconfirm>
                          <AntButton
                            type="primary"
                            size="small"
                            loading={applying}
                            id="apply-receipt-submit"
                            disabled={(() => {
                              const maxByBalance = Number(receiptDetail?.잔액) || 0;
                              const maxByDue = Number(depositSummary?.미수금) || 0;
                              const maxAllowed = Math.max(0, Math.min(maxByBalance, maxByDue));
                              const amt = Number(applyAmount) || 0;
                              return !selectedReceiptTxNo || amt <= 0 || amt > maxAllowed;
                            })()}
                            onClick={async () => {
                              try {
                                if (!selectedReceiptTxNo) { message.error('선택된 수납이 없습니다.'); return; }
                                const maxByBalance = Number(receiptDetail?.잔액) || 0;
                                const maxByDue = Number(depositSummary?.미수금) || 0;
                                const maxAllowed = Math.max(0, Math.min(maxByBalance, maxByDue));
                                const amt = Number(applyAmount) || 0;
                                if (amt <= 0) { message.error('적용할 금액을 입력하세요.'); return; }
                                if (amt > maxAllowed) { message.error('최대 적용 가능 금액을 초과했습니다.'); return; }
                                setApplying(true);
                                const hide = message.loading('입금 적용 중...', 0);
                                await applyReceiptToDeposit({
                                  saleNo,
                                  transactionNo: selectedReceiptTxNo,
                                  amount: amt,
                                  note: applyNote,
                                  currencyCode: receiptDetail?.수납통화코드 || '058001',
                                  cashReceiptSelfExclude: false,
                                });
                                hide();
                                message.success('입금이 반영되었습니다.');
                                await refreshPaymentModalData();
                              } catch (e) {
                                message.error(e?.message || '입금 적용에 실패했습니다.');
                              } finally {
                                setApplying(false);
                              }
                            }}
                          >
                            입금 적용
                          </AntButton>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </AntModal>
        <AntModal
          open={paymentHelpOpen}
          onCancel={() => setPaymentHelpOpen(false)}
          footer={null}
          centered
          width={640}
          title={"입금내역 편집 도움말"}
        >
          <div className="space-y-4 text-xs text-gray-700">
            <div>
              <div className="font-semibold mb-1">1. 상단 요약 정보</div>
              <p className="mb-0">현재 미수금은 아직 받지 못한 금액, 현재 입금액은 이 예약에 이미 입금으로 처리된 금액입니다.</p>
            </div>
            <div>
              <div className="font-semibold mb-1">2. 입금 내역</div>
              <p className="mb-0">이 예약에 이미 적용된 입금 기록입니다. 필요 시 행 우측의 삭제 버튼으로 개별 삭제할 수 있습니다.</p>
            </div>
            <div>
              <div className="font-semibold mb-1">3. 통장 입금 내역</div>
              <p className="mb-0">실제 통장/카드 기준으로 기록된 전체 입금 리스트입니다. 여기서 예약에 사용할 입금 건을 선택합니다.</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2">
              <div className="font-semibold text-[11px]">3-1. 새 입금 등록</div>
              <p className="mb-0">실제로 새로 입금된 금액이 있을 때 통장 입금 내역에 새 행을 추가하는 영역입니다.</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>입금일 / 입금액 / 입금자 / 입금 계좌를 필수로 입력합니다.</li>
                <li>비고사항에는 선금/잔금 등 메모를 남길 수 있습니다.</li>
                <li>"등록 후 바로 적용" 체크 시, 새 입금을 통장에 등록하면서 동시에 현재 예약의 입금으로도 즉시 반영합니다.</li>
              </ul>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2">
              <div className="font-semibold text-[11px]">3-2. 선택 입금 내역 적용</div>
              <p className="mb-0">통장 입금 내역에서 한 건을 선택한 뒤, 이 예약에 얼마를 적용할지 결정하는 영역입니다.</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>최대 적용 가능 금액 = 해당 입금 건의 잔액과 현재 미수금 중 더 작은 값입니다.</li>
                <li>적용할 금액은 이 범위 안에서 입력해야 하며, [입금 적용] 버튼을 누르면 예약의 입금액과 미수금이 갱신됩니다.</li>
                <li>이 방식은 기존 통장 입금 내역을 그대로 두고, 해당 금액만 이 예약에 배분하여 사용하는 것입니다.</li>
              </ul>
            </div>
            <div className="border-t pt-3 mt-2 text-[11px] text-gray-500 space-y-1">
              <div className="font-semibold">요약</div>
              <p className="mb-0">- 새로 들어온 돈을 기록하려면 "새 입금 등록"을 사용합니다.</p>
              <p className="mb-0">- 이미 통장에 있는 입금을 이 예약에만 배분하려면, 통장 입금 내역에서 선택 후 "선택 입금 내역 적용"을 사용합니다.</p>
            </div>
          </div>
        </AntModal>

        {/* 행사 전도금 내역 Modal */}


        <AntModal
          open={logsModalOpen}
          onCancel={() => setLogsModalOpen(false)}
          footer={null}
          centered
          width={720}
          title={"예약기록"}
        >
          {logsLoading && <div className="text-sm text-gray-500">불러오는 중...</div>}
          {!logsLoading && logsError && <div className="text-sm text-red-600">{logsError}</div>}
          {!logsLoading && !logsError && (
            <div className="space-y-2">
              {(() => {
                const modalRows = (reservationLogs || []).filter((r) => String(r?.태그 || '').trim() !== '업무메모');
                if (modalRows.length === 0) {
                  return <div className="text-xs text-gray-500">표시할 예약기록이 없습니다.</div>;
                }
                return (
                  <Table
                    size="small"
                    bordered
                    pagination={false}
                    sticky
                    scroll={{ y: 360 }}
                    dataSource={modalRows.map((r, idx) => ({ key: idx, ...r }))}
                    columns={[
                      { title: '태그', dataIndex: '태그', key: '태그', width: 100, align: 'center' },
                      { title: '내용', dataIndex: '내용', key: '내용' },
                      { title: '작성일', dataIndex: '작성일', key: '작성일', width: 160, align: 'center' },
                    ]}
                  />
                );
              })()}
            </div>
          )}
        </AntModal>

        {/* 업무메모 추가 모달 */}
        <AntModal
          open={memoAddOpen}
          onCancel={() => {
            if (memoAdding) return;
            setMemoAddOpen(false);
            setMemoAddText('');
          }}
          centered
          title={"업무메모 추가"}
          maskClosable={false}
          okText={"저장"}
          cancelText={"취소"}
          confirmLoading={memoAdding}
          onOk={async () => {
            try {
              if (!saleNo) { message.error('판매번호가 없어 업무메모를 추가할 수 없습니다.'); return; }
              const nextMemo = String(memoAddText ?? '').trim();
              if (!nextMemo) { message.error('메모 내용을 입력하세요.'); return; }
              setMemoAdding(true);
              const hide = message.loading('업무메모 추가 중...', 0);
              await createReservationMemoLog({
                saleNo,
                memo: nextMemo,
                vendorViewable: true,
                dispatcherViewable: true,
              });
              hide();
              message.success('추가되었습니다.');
              setMemoAddOpen(false);
              setMemoAddText('');
              await loadReservationLogs({ silent: true });
            } catch (e) {
              message.error(e?.message || '추가에 실패했습니다.');
            } finally {
              setMemoAdding(false);
            }
          }}
        >
          <AntInput.TextArea
            value={memoAddText}
            onChange={(e) => setMemoAddText(e.target.value)}
            autoSize={{ minRows: 6, maxRows: 12 }}
            placeholder="업무메모 내용을 입력하세요"
          />
        </AntModal>

        {/* 업무메모 수정 모달 */}
        <AntModal
          open={memoEditOpen}
          onCancel={() => {
            if (memoSaving || memoDeleting) return;
            setMemoEditOpen(false);
            setMemoEditRow(null);
            setMemoEditText('');
          }}
          centered
          title={"업무메모 수정"}
          maskClosable={false}
          footer={(
            <div className="flex items-center justify-between">
              <div>
                {(() => {
                  const authorNo = getLogAuthorUserNo(memoEditRow);
                  const canDelete = authorNo && currentUserNo && authorNo === currentUserNo;
                  const referenceNo = getLogReferenceNo(memoEditRow);
                  const deleteDisabled = !referenceNo;
                  if (!canDelete) return <span />;
                  return (
                    <Popconfirm
                      title="업무메모 삭제"
                      description="정말 삭제하시겠습니까?"
                      okText="삭제"
                      cancelText="취소"
                      onConfirm={async () => {
                        try {
                          if (!memoEditRow) { message.error('삭제 대상 메모가 없습니다.'); return; }
                          const aNo = getLogAuthorUserNo(memoEditRow);
                          if (!aNo || aNo !== currentUserNo) { message.error('작성자만 삭제할 수 있습니다.'); return; }
                          const refNo = getLogReferenceNo(memoEditRow);
                          if (!refNo) { message.error('참조번호가 없어 삭제할 수 없습니다.'); return; }
                          setMemoDeleting(true);
                          const hide = message.loading('업무메모 삭제 중...', 0);
                          await deleteReservationMemoLog({ referenceNo: refNo });
                          hide();
                          message.success('삭제되었습니다.');
                          setMemoEditOpen(false);
                          setMemoEditRow(null);
                          setMemoEditText('');
                          await loadReservationLogs({ silent: true });
                        } catch (e) {
                          message.error(e?.message || '삭제에 실패했습니다.');
                        } finally {
                          setMemoDeleting(false);
                        }
                      }}
                      disabled={deleteDisabled || memoDeleting || memoSaving}
                    >
                      <AntButton danger disabled={deleteDisabled || memoDeleting || memoSaving} loading={memoDeleting}>
                        삭제
                      </AntButton>
                    </Popconfirm>
                  );
                })()}
              </div>
              <div className="flex items-center gap-2">
                <AntButton onClick={() => {
                  if (memoSaving || memoDeleting) return;
                  setMemoEditOpen(false);
                  setMemoEditRow(null);
                  setMemoEditText('');
                }}>
                  취소
                </AntButton>
                <AntButton type="primary" loading={memoSaving} onClick={async () => {
                  try {
                    if (!memoEditRow) { message.error('수정 대상 메모가 없습니다.'); return; }
                    const authorNo = getLogAuthorUserNo(memoEditRow);
                    if (!authorNo || authorNo !== currentUserNo) {
                      message.error('작성자만 수정할 수 있습니다.');
                      return;
                    }
                    const referenceNo = getLogReferenceNo(memoEditRow);
                    if (!referenceNo) { message.error('참조번호가 없어 수정할 수 없습니다.'); return; }

                    const nextMemo = String(memoEditText ?? '').trim();
                    if (!nextMemo) { message.error('메모 내용을 입력하세요.'); return; }

                    setMemoSaving(true);
                    const hide = message.loading('업무메모 수정 중...', 0);
                    await updateReservationMemoLog({
                      referenceNo,
                      memo: nextMemo,
                      vendorViewable: memoEditRow?.판매업체조회가능 ?? true,
                      dispatcherViewable: memoEditRow?.수배업체조회가능 ?? true,
                    });
                    hide();
                    message.success('수정되었습니다.');
                    setMemoEditOpen(false);
                    setMemoEditRow(null);
                    setMemoEditText('');
                    await loadReservationLogs({ silent: true });
                  } catch (e) {
                    message.error(e?.message || '수정에 실패했습니다.');
                  } finally {
                    setMemoSaving(false);
                  }
                }}>
                  저장
                </AntButton>
              </div>
            </div>
          )}
        >
          <AntInput.TextArea
            value={memoEditText}
            onChange={(e) => setMemoEditText(e.target.value)}
            autoSize={{ minRows: 6, maxRows: 12 }}
            placeholder="업무메모 내용을 입력하세요"
          />
        </AntModal>

        {/* Cancel Fee Modal */}
        <AntModal
          open={cancelModalOpen}
          onCancel={() => setCancelModalOpen(false)}
          footer={null}
          centered
          maskClosable={false}
          width={480}
          title={"예약취소 처리"}
        >
          {cancelInfoLoading && <div className="text-sm text-gray-500">불러오는 중...</div>}
          {!cancelInfoLoading && cancelInfoError && <div className="text-sm text-red-600">{cancelInfoError}</div>}
          {!cancelInfoLoading && !cancelInfoError && (
            <div className="space-y-4">
              <div>
                <div className="text-[11px] text-gray-500 mb-1">예상 취소료</div>
                <AntInputNumber
                  size="small"
                  min={0}
                  value={cancelFee}
                  onChange={(v) => setCancelFee(Number(v) || 0)}
                  style={{ width: '100%' }}
                  formatter={(val) => {
                    const n = typeof val === 'number' ? val : Number(val || 0);
                    return n.toLocaleString('ko-KR');
                  }}
                  parser={(val) => (val ? val.toString().replace(/,/g, '') : '')}
                />
              </div>
              <div className="flex justify-end gap-2">
                <AntButton size="small" onClick={() => setCancelModalOpen(false)}>닫기</AntButton>
                <AntButton
                  type="primary"
                  size="small"
                  loading={cancelSaving}
                  onClick={async () => {
                    if (!saleNo) { message.error('판매번호가 없어 취소를 저장할 수 없습니다.'); return; }
                    let hide;
                    try {
                      setCancelSaving(true);
                      hide = message.loading('취소 처리 중...', 0);
                      await cancelSale({ saleNo, cancelFee });
                      if (hide) hide();
                      message.success('예약이 취소되었습니다.');
                      setCancelModalOpen(false);
                      setCancelLocal('1');
                      // Refresh sale status from server
                      try {
                        setStatusLoading(true);
                        const row = await getOrderSaleStatus({ orderNo });
                        setSaleStatusRow(row || null);
                      } finally {
                        setStatusLoading(false);
                      }
                      // Refresh price info immediately
                      try {
                        setPriceLoading(true);
                        setPriceError(null);
                        const latestPrice = await getReservationPriceInfo({ saleNo });
                        setPriceInfo(latestPrice || null);
                        setCancelFeeDraft(Number(latestPrice?.취소료원화) || 0);
                      } catch (e2) {
                        setPriceError(e2?.message || '가격 정보를 불러오지 못했습니다.');
                      } finally {
                        setPriceLoading(false);
                      }
                    } catch (e) {
                      message.error(e?.message || '취소 처리에 실패했습니다.');
                    } finally {
                      if (hide) hide();
                      setCancelSaving(false);
                    }
                  }}
                >
                  저장
                </AntButton>
              </div>
            </div>
          )}
        </AntModal>

        {/* Group info drawer (reusable) */}
        <GroupInfoDrawer
          open={groupOpen}
          onClose={() => { setGroupOpen(false); setGroupPreDepAutoOpen(false); setDirectPreDepositMode(false); }}
          saleNo={saleNo}
          title={location?.state?.productName || info?.판매상품명 || '단체정보'}
          preDepositAutoOpen={groupPreDepAutoOpen}
          directPreDepositMode={directPreDepositMode}
        />

        <InvoiceModal
          open={invoiceOpen}
          onClose={() => setInvoiceOpen(false)}
          reservationInfo={info}
          saleInfo={saleInfoRow}
          priceInfo={priceInfo}
          depositSummary={invoiceDepositSummary}
          orderNo={orderNo}
          saleNo={saleNo}
          travelers={travelers}
          extraServices={extraServices}
          ordererEmail={ordererEmail || info?.주문자이메일 || info?.이메일 || ''}
        />

        {/* 여행객 일괄수정 모달 */}
        <BulkEditTravelerModal
          open={bulkEditModalOpen}
          onClose={() => setBulkEditModalOpen(false)}
          selectedTravelers={travelers.filter((t) => selectedTravelerKeys.includes(t.key))}
          onApply={async (changes) => {
            const { productPrice, costPrice, taxFuel, statusAction, cancelFee } = changes || {};
            const nextTravelers = (travelers || []).map((t) => {
              if (!selectedTravelerKeys.includes(t.key)) return t;
              let next = { ...t };
              // 상품가
              if (productPrice) {
                const current = Number(next.상품금액원화) || 0;
                if (productPrice.mode === 'overwrite') next.상품금액원화 = productPrice.value;
                if (productPrice.mode === 'add') next.상품금액원화 = current + productPrice.value;
                if (productPrice.mode === 'subtract') next.상품금액원화 = Math.max(0, current - productPrice.value);
              }
              // 원가
              if (costPrice) {
                const current = Number(next.공급단가) || 0;
                if (costPrice.mode === 'overwrite') next.공급단가 = costPrice.value;
                if (costPrice.mode === 'add') next.공급단가 = current + costPrice.value;
                if (costPrice.mode === 'subtract') next.공급단가 = Math.max(0, current - costPrice.value);
              }
              // 택스 및 유류
              if (taxFuel) {
                const current = Number(next.택스및유류원화) || 0;
                if (taxFuel.mode === 'overwrite') next.택스및유류원화 = taxFuel.value;
                if (taxFuel.mode === 'add') next.택스및유류원화 = current + taxFuel.value;
                if (taxFuel.mode === 'subtract') next.택스및유류원화 = Math.max(0, current - taxFuel.value);
              }
              // 상태 변경 + 취소료 적용
              if (statusAction === 'cancel') {
                next.여행객종류 = '취소';
                if (cancelFee && typeof cancelFee.value === 'number') {
                  next.상품금액원화 = cancelFee.value;
                  next.택스및유류원화 = 0;
                }
              }
              if (statusAction === 'restore') next.여행객종류 = '여행객';
              if (statusAction === 'delete') return null; // 나중에 필터링
              return next;
            }).filter(Boolean);

            setTravelers(nextTravelers);
            await handleSave(nextTravelers);
          }}
        />

        {/* 룸번호 편집 모달 */}
        <RoomAssignmentModal
          open={roomModalOpen}
          onClose={() => setRoomModalOpen(false)}
          travelers={travelers}
          editLocked={editLocked}
          productName={productName}
          departureDate={saleDepartureDate}
          onSave={async (updatedTravelers) => {
            // 룸번호가 업데이트된 여행객 리스트로 전체 저장
            if (saleNo == null) return;
            setSaving(true);
            try {
              const fieldRows = [];
              const amountRows = [];
              const orderRows = [];
              (updatedTravelers || []).forEach((t) => {
                const travelerNo = Number(t?.여행객번호 || t?.여행객키);
                if (!travelerNo) return;
                const pushField = (fieldNo, val) => fieldRows.push({ 여행객번호: travelerNo, 필드번호: fieldNo, 값: val });
                // 필드 매핑
                pushField(8, t?.한글이름 ?? null); // 한글이름
                pushField(-1, t?.룸번호 ?? null); // 룸번호
                pushField(109, t?.성별 ?? null); // 성별
                pushField(9, t?.영문성 ?? null); // 영문성
                pushField(10, t?.영문이름 ?? null); // 영문이름
                pushField(11, t?.생년월일 ?? null); // 생년월일
                pushField(13, null);
                pushField(253, t?.여권번호 ?? null); // 여권번호
                pushField(254, t?.여권만료일 ?? null); // 여권만료일
                pushField(314, t?.여행객비고 ?? null); // 비고
                pushField(12, t?.휴대폰 ?? null);
                pushField(256, t?.이메일 ?? null);
                pushField(404, null); // 주민등록번호 공란
                pushField(255, null);

                // 금액 정보
                amountRows.push({
                  여행객번호: travelerNo,
                  상품금액: Number(t?.상품금액원화) || 0,
                  공급단가: Number(t?.공급단가) || 0,
                  택스및유류: Number(t?.택스및유류원화) || 0,
                });

                // 순번/확인 정보
                orderRows.push({
                  여행객번호: travelerNo,
                  여행객종류: t?.여행객종류 ?? '여행객',
                  선발권동의: !!t?.선발권동의,
                  명단확인: !!t?.명단확인,
                  순번: Number.isFinite(Number(t?.순번)) ? Number(t?.순번) : (orderRows.length + 1),
                });
              });

              await saveReservationTravelerChanges({
                saleNo,
                travelerFieldRows: fieldRows,
                travelerAmounts: amountRows,
                travelerOrders: orderRows,
              });
              // Refresh traveler list after save
              const updated = await getReservationTravelerListBySaleNo({ saleNo });
              const withKeys = (Array.isArray(updated) ? updated : []).map((r, idx) => ({ ...r, key: String(r?.여행객번호 || r?.여행객키 || idx) }));
              setTravelers(withKeys);
            } finally {
              setSaving(false);
            }
          }}
        />

      </div>
    </ConfigProvider>
  );
};

export default ReservationDetail;
